import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { SquaresFour, Plus, Trash, User, ForkKnife, CreditCard, PencilSimple, Users, Sliders, Desktop, WifiHigh, Percent, Buildings, ImageSquare, Database, Broom, Crown, QrCode, Printer, DotsSixVertical, Star, Bell, PaperPlaneTilt, ChatCircle, Truck, MapPin } from "@phosphor-icons/react";
import LoyaltyTiersCard from "@/components/LoyaltyTiersCard";
import TipPoolCard from "@/components/settings/TipPoolCard";
import GiftCardsCard from "@/components/settings/GiftCardsCard";
import ModifierTemplateForm from "@/components/settings/ModifierTemplateForm";
import SystemHealthCard from "@/components/settings/SystemHealthCard";
import BuildInfoChip from "@/components/settings/BuildInfoChip";
import DatabaseHealthCard from "@/components/settings/DatabaseHealthCard";
import DataArchiveCard from "@/components/settings/DataArchiveCard";
import DraggableFloorEditor from "@/components/settings/DraggableFloorEditor";
import MenuItemsReorder from "@/components/settings/MenuItemsReorder";
import MenuCsvImport from "@/components/settings/MenuCsvImport";
import BulkAvailabilityButton from "@/components/settings/BulkAvailabilityButton";
import DeliverySettings from "@/components/settings/DeliverySettings";
import DeliveryMenu from "@/components/settings/DeliveryMenu";
import DeliveryHeatmap from "@/components/settings/DeliveryHeatmap";
import { DoorDashCard, UberEatsCard, GrubhubCard } from "@/components/settings/ThirdPartyDeliveryCard";
import { AutoDispatchCard } from "@/components/settings/AutoDispatchCard";
import EodRecipientChips from "@/components/settings/EodRecipientChips";
import BrandingPreview from "@/components/settings/BrandingPreview";
import LogoCropModal from "@/components/settings/LogoCropModal";
import { autoPadLogo } from "@/utils/autoPadLogo";
import { PosSettingsContent } from "@/pages/PosSettingsPage";
import BarcodeField from "@/components/settings/BarcodeField";
import PerTableQRCodesCard from "@/components/settings/PerTableQRCodesCard";
import PublicOrderLinkCard from "@/components/settings/PublicOrderLinkCard";
import PaymentGatewayCard from "@/components/settings/PaymentGatewayCard";
import { QRCodeSVG } from "qrcode.react";
import { motion, Reorder } from "framer-motion";
import { optimizeImageUrl } from "@/utils/imageOptimizer";
import { pairPrinter, webusbSupported } from "@/utils/webusbPrinter";

// ---------------------------------------------------------------------------
// Settings navigation config
// ---------------------------------------------------------------------------
// SINGLE source of truth for the two-level Settings nav. Adding a new sub-tab
// is a one-line append here — the SUB_TO_CATEGORY reverse map (used by the
// sync useEffect) is derived automatically so it can never drift.
export const SETTINGS_CATEGORIES = [
  {
    key: "restaurant",
    label: "Company / Restaurant",
    subs: ["company", "layout", "loyalty", "tax", "tip-pool", "online-ordering", "notifications"],
  },
  {
    key: "menu",
    label: "Menu",
    subs: ["menu", "modifiers", "gift-cards"],
  },
  {
    key: "equipment",
    label: "Equipment",
    subs: ["equipment", "network", "payment", "qr-codes", "pos-settings", "database"],
  },
];
export const SETTINGS_CATEGORY_MAP = SETTINGS_CATEGORIES.reduce((acc, cat) => {
  cat.subs.forEach((s) => { acc[s] = cat.key; });
  return acc;
}, {});

const SettingsPage = () => {
  const { user } = useAuth();
  const [tables, setTables] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [modifierTemplates, setModifierTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState("Main Floor");

  // Dialogs
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [showMenuDialog, setShowMenuDialog] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showRoomDialog, setShowRoomDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingMenu, setEditingMenu] = useState(null);
  const [showModifierDialog, setShowModifierDialog] = useState(false);
  const [editingModifier, setEditingModifier] = useState(null);

  // Forms
  const [newTable, setNewTable] = useState({ name: "", seats: 4, shape: "square", room: "Main Floor" });
  const [newMenuItem, setNewMenuItem] = useState({ name: "", description: "", price: "", category: "mains", image_url: "", available: true, modifier_groups: [], barcode: "" });
  const [newUser, setNewUser] = useState({ name: "", pin: "", role: "server", email: "", hourly_rate: "" });
  const [newRoom, setNewRoom] = useState("");

  // Equipment & Network state
  const [equipment, setEquipment] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [showEquipDialog, setShowEquipDialog] = useState(false);
  const [editingEquip, setEditingEquip] = useState(null);
  const [equipForm, setEquipForm] = useState({ name: "", type: "printer", model_name: "", serial_number: "", ip_address: "", mac_address: "", connection_type: "network", device_path: "", webusb_vendor_id: null, webusb_product_id: null, webusb_serial_number: "", webusb_display_name: "", location: "", assigned_station: "", assigned_printer: "", status: "offline", notes: "" });
  const [showNetDialog, setShowNetDialog] = useState(false);
  const [editingNet, setEditingNet] = useState(null);
  const [netForm, setNetForm] = useState({ name: "", ssid: "", password: "", security_type: "WPA2", ip_range: "", subnet_mask: "255.255.255.0", gateway: "", dns_primary: "", dns_secondary: "", vlan_id: "", purpose: "", notes: "" });
  const [showNetPasswords, setShowNetPasswords] = useState({});
  // USB printer device auto-detect (populated when the Add/Edit Device
  // dialog opens for a printer).
  const [usbPrinterDevices, setUsbPrinterDevices] = useState([]);
  // Collapse the rarely-used fields (Model, Serial, MAC, Location, Station,
  // Notes) behind a "Show more details" toggle so a new printer can be
  // added with just Name + IP.
  const [showEquipAdvanced, setShowEquipAdvanced] = useState(false);
  // Detect-from-IP status for network printers.
  const [ipProbing, setIpProbing] = useState(false);
  // Two-level Settings nav: category (Company/Menu/Equipment) + active sub-tab.
  // Persisted in localStorage so a manager who's editing the menu doesn't get
  // bounced back to Company on every page reload.
  const [settingsCategory, setSettingsCategory] = useState(() => {
    try { return localStorage.getItem("crest.settings.category") || "restaurant"; } catch { return "restaurant"; }
  });
  const [settingsSubTab, setSettingsSubTab] = useState(() => {
    try { return localStorage.getItem("crest.settings.subtab") || "company"; } catch { return "company"; }
  });
  useEffect(() => {
    try { localStorage.setItem("crest.settings.category", settingsCategory); } catch { /* ignore */ }
  }, [settingsCategory]);
  useEffect(() => {
    try { localStorage.setItem("crest.settings.subtab", settingsSubTab); } catch { /* ignore */ }
  }, [settingsSubTab]);

  // Keep the top-level category in sync with the active sub-tab (e.g. if
  // the user deep-links or a persisted sub-tab was moved to a new
  // category between deploys). Runs after render to avoid setState in
  // render. Derived from SETTINGS_CATEGORY_MAP so the mapping never
  // drifts from the CATEGORIES definition below.
  useEffect(() => {
    if (settingsSubTab === "notifications" && !notifConfig) {
      loadNotifConfig();
    }
     
  }, [settingsSubTab]);

  useEffect(() => {
    const shouldBe = SETTINGS_CATEGORY_MAP[settingsSubTab];
    if (shouldBe && shouldBe !== settingsCategory) setSettingsCategory(shouldBe);
  }, [settingsSubTab, settingsCategory]);
  // Per-terminal default printer — stored in localStorage so each POS
  // station can pick its own default without touching the DB (one printer
  // may be "default" for the bar terminal and another for the kitchen).
  const DEFAULT_PRINTER_KEY = "crest.receipt.defaultPrinterId";
  const [defaultPrinterId, setDefaultPrinterId] = useState(() => {
    try { return localStorage.getItem(DEFAULT_PRINTER_KEY) || ""; } catch { return ""; }
  });
  const toggleDefaultPrinter = (id) => {
    try {
      if (defaultPrinterId === id) {
        localStorage.removeItem(DEFAULT_PRINTER_KEY);
        setDefaultPrinterId("");
        toast.success("Default printer cleared for this terminal");
      } else {
        localStorage.setItem(DEFAULT_PRINTER_KEY, id);
        setDefaultPrinterId(id);
        toast.success("Default printer set for this terminal");
      }
    } catch {
      toast.error("Couldn't save default (browser storage disabled?)");
    }
  };
  // Wi-Fi scan
  const [scanLoading, setScanLoading] = useState(false);
  const [scanResults, setScanResults] = useState(null); // { mocked, source, networks: [{ssid, signal, security}] }
  const [storeSettings, setStoreSettings] = useState({ tax_rate: 8.0, company_name: "", company_address: "", company_city: "", company_state: "", company_zip: "", company_phone: "", company_email: "", logo_url: "", website_title: "" });
  // Notifications config (Twilio/SendGrid). Loaded lazily when admin opens the tab.
  const [notifConfig, setNotifConfig] = useState(null);
  const [notifDraft, setNotifDraft] = useState({});
  const [notifTest, setNotifTest] = useState({ channel: "sms", to: "" });
  const [notifTestLoading, setNotifTestLoading] = useState(false);
  // Month-to-date SMS spend meter — populated when admin opens the tab.
  const [smsUsage, setSmsUsage] = useState(null);

  const loadSmsUsage = async () => {
    try {
      const res = await axios.get(`${API}/notifications-config/sms-usage`);
      setSmsUsage(res.data);
    } catch {
      // silently ignore — the meter simply hides
    }
  };

  const loadNotifConfig = async () => {
    try {
      const res = await axios.get(`${API}/notifications-config`);
      setNotifConfig(res.data);
      setNotifDraft({
        twilio_account_sid: res.data.twilio_account_sid || "",
        twilio_auth_token: "",
        twilio_from_number: res.data.twilio_from_number || "",
        sendgrid_api_key: "",
        sendgrid_from_email: res.data.sendgrid_from_email || "",
        sendgrid_from_name: res.data.sendgrid_from_name || "",
        order_confirmation_enabled: !!res.data.order_confirmation_enabled,
        order_ready_enabled: !!res.data.order_ready_enabled,
        dine_in_notifications: !!res.data.dine_in_notifications,
        review_request_enabled: !!res.data.review_request_enabled,
        review_url: res.data.review_url || "",
        review_delay_minutes: res.data.review_delay_minutes ?? 20,
        public_base_url: res.data.public_base_url || "",
        twilio_sms_rate_usd: res.data.twilio_sms_rate_usd ?? 0.0079,
        eod_email_enabled: !!res.data.eod_email_enabled,
        // Recipients are now [{email,enabled}] objects — the setter below
        // owns add/remove/toggle so we hand the raw array to the draft.
        eod_email_recipients: (res.data.eod_email_recipients || []).map((r) =>
          typeof r === "string"
            ? { email: r, enabled: true }
            : { email: r.email, enabled: r.enabled !== false }
        ),
        eod_email_hour: res.data.eod_email_hour ?? 23,
        eod_email_minute: res.data.eod_email_minute ?? 30,
        eod_email_timezone: res.data.eod_email_timezone || "UTC",
      });
      loadSmsUsage();
    } catch (e) {
      toast.error("Failed to load notifications config");
    }
  };

  const saveNotifConfig = async () => {
    try {
      // Recipients are stored as [{email, enabled}] objects — the API accepts
      // that shape directly (and still tolerates legacy strings).
      const recipients = (notifDraft.eod_email_recipients || [])
        .map((r) => ({
          email: (r.email || "").trim(),
          enabled: r.enabled !== false,
        }))
        .filter((r) => r.email);
      const payload = {
        ...notifDraft,
        eod_email_recipients: recipients,
        eod_email_hour: Number(notifDraft.eod_email_hour ?? 23),
        eod_email_minute: Number(notifDraft.eod_email_minute ?? 30),
      };
      const res = await axios.put(`${API}/notifications-config`, payload);
      setNotifConfig(res.data);
      setNotifDraft((d) => ({ ...d, twilio_auth_token: "", sendgrid_api_key: "" }));
      toast.success("Notifications settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const sendEodNow = async () => {
    try {
      const res = await axios.post(`${API}/notifications-config/eod-send-now`, {});
      if (res.data?.skipped) {
        toast.error(`Not sent: ${res.data.skipped}`);
        return;
      }
      const results = res.data?.results || [];
      const okCount = results.filter((r) => r.ok).length;
      const skipped = results.filter((r) => r.skipped).length;
      let msg = `End of Day sent to ${okCount} recipient(s)`;
      if (skipped) msg += ` · ${skipped} disabled`;
      toast.success(msg);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };

  const [eodPreview, setEodPreview] = useState(null);
  const loadEodPreview = async () => {
    try {
      const res = await axios.get(`${API}/notifications-config/eod-preview`);
      setEodPreview(res.data);
    } catch (e) {
      toast.error("Preview failed");
    }
  };

  const sendTestNotification = async () => {
    if (!notifTest.to.trim()) {
      toast.error(`Enter a ${notifTest.channel === "sms" ? "phone number" : "email"} to test`);
      return;
    }
    setNotifTestLoading(true);
    try {
      const res = await axios.post(`${API}/notifications-config/test`, notifTest);
      if (res.data.ok) toast.success(`Test ${notifTest.channel} sent!`);
      else toast.error(`Test failed: ${res.data.detail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally {
      setNotifTestLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [tablesRes, menuRes, catsRes, usersRes, roomsRes, locsRes, modTmplRes, equipRes, netRes, settingsRes] = await Promise.all([
        axios.get(`${API}/tables`),
        axios.get(`${API}/menu`),
        axios.get(`${API}/menu-categories`).catch(() => ({ data: [] })),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/rooms`).catch(() => ({ data: [] })),
        axios.get(`${API}/locations`).catch(() => ({ data: [] })),
        axios.get(`${API}/modifier-templates`).catch(() => ({ data: [] })),
        axios.get(`${API}/equipment`).catch(() => ({ data: [] })),
        axios.get(`${API}/network`).catch(() => ({ data: [] })),
        axios.get(`${API}/store-settings`).catch(() => ({ data: { tax_rate: 8.0, company_name: "", company_address: "", company_city: "", company_state: "", company_zip: "", company_phone: "", company_email: "", logo_url: "" } }))
      ]);
      setTables(tablesRes.data);
      setMenuItems(menuRes.data);
      setMenuCategories(catsRes.data || []);
      setUsers(usersRes.data);
      setRooms(roomsRes.data);
      setLocations(locsRes.data);
      setModifierTemplates(modTmplRes.data);
      setEquipment(equipRes.data);
      setNetworks(netRes.data);
      setStoreSettings(settingsRes.data);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  // ========== TABLE MANAGEMENT ==========
  const handleCreateTable = async () => {
    try {
      await axios.post(`${API}/tables`, { ...newTable, position: { x: 100, y: 100 } });
      toast.success("Table created");
      setShowTableDialog(false);
      setNewTable({ name: "", seats: 4, shape: "square", room: selectedRoom });
      fetchData();
    } catch (error) { toast.error("Failed to create table"); }
  };

  const handleDeleteTable = async (tableId) => {
    try {
      await axios.delete(`${API}/tables/${tableId}`);
      toast.success("Table deleted");
      fetchData();
    } catch (error) { toast.error("Failed to delete table"); }
  };

  const handleTableDrag = async (tableId, position) => {
    try {
      await axios.put(`${API}/tables/${tableId}`, { position });
      setTables(tables.map(t => t.id === tableId ? { ...t, position } : t));
    } catch (error) { toast.error("Failed to update position"); }
  };

  // ========== ROOM MANAGEMENT ==========
  const handleCreateRoom = async () => {
    if (!newRoom.trim()) return;
    try {
      await axios.post(`${API}/rooms`, { name: newRoom.trim() });
      toast.success("Room created");
      setShowRoomDialog(false);
      setNewRoom("");
      fetchData();
    } catch (error) { toast.error("Failed to create room"); }
  };

  const handleDeleteRoom = async (roomId) => {
    try {
      await axios.delete(`${API}/rooms/${roomId}`);
      toast.success("Room deleted");
      fetchData();
    } catch (error) { toast.error("Failed to delete room"); }
  };

  const handleReorderRooms = async (newOrder) => {
    // Optimistic: paint the new order immediately, then persist.
    setRooms(newOrder);
    try {
      await axios.put(`${API}/rooms/reorder`, { order: newOrder.map((r) => r.id) });
    } catch (error) {
      toast.error("Failed to save floor order");
      fetchData();
    }
  };

  // ========== MENU MANAGEMENT ==========
  const handleCreateMenuItem = async () => {
    try {
      // Trim barcode so surrounding whitespace from a scanner doesn't
      // create false uniqueness collisions with the same code typed
      // in manually.
      const payload = {
        ...newMenuItem,
        price: parseFloat(newMenuItem.price),
        barcode: (newMenuItem.barcode || "").trim() || null,
      };
      await axios.post(`${API}/menu`, payload);
      toast.success("Menu item created");
      setShowMenuDialog(false);
      setNewMenuItem({ name: "", description: "", price: "", category: "mains", image_url: "", available: true, modifier_groups: [], barcode: "" });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to create menu item"); }
  };

  const handleUpdateMenuItem = async () => {
    if (!editingMenu) return;
    try {
      const updates = {
        name: editingMenu.name,
        description: editingMenu.description,
        price: parseFloat(editingMenu.price),
        category: editingMenu.category,
        image_url: editingMenu.image_url,
        available: editingMenu.available,
        modifier_groups: editingMenu.modifier_groups || [],
        modifiers: (editingMenu.modifiers || []).map(m => ({
          name: (m.name || "").trim(),
          price: parseFloat(m.price) || 0,
        })).filter(m => m.name),
        location_prices: editingMenu.location_prices || [],
        // Empty string clears the barcode on backend (see routes/menu.py update).
        barcode: (editingMenu.barcode || "").trim(),
      };
      await axios.put(`${API}/menu/${editingMenu.id}`, updates);
      toast.success("Menu item updated");
      setEditingMenu(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to update menu item"); }
  };

  const handleDeleteMenuItem = async (itemId) => {
    try { await axios.delete(`${API}/menu/${itemId}`); toast.success("Deleted"); fetchData(); }
    catch (error) { toast.error("Failed"); }
  };

  const handleToggleAvailability = async (item) => {
    try { await axios.put(`${API}/menu/${item.id}`, { available: !item.available }); fetchData(); }
    catch (error) { toast.error("Failed"); }
  };

  // ========== MODIFIER TEMPLATE MANAGEMENT ==========
  const handleCreateModifierTemplate = async (tmplData) => {
    try {
      await axios.post(`${API}/modifier-templates`, tmplData);
      toast.success("Modifier group created");
      setShowModifierDialog(false);
      fetchData();
    } catch (error) { toast.error("Failed to create modifier group"); }
  };

  const handleUpdateModifierTemplate = async () => {
    if (!editingModifier) return;
    try {
      await axios.put(`${API}/modifier-templates/${editingModifier.id}`, {
        group_name: editingModifier.group_name,
        required: editingModifier.required,
        min_select: parseInt(editingModifier.min_select) || 0,
        max_select: parseInt(editingModifier.max_select) || 0,
        options: editingModifier.options
      });
      toast.success("Modifier group updated");
      setEditingModifier(null);
      fetchData();
    } catch (error) { toast.error("Failed to update"); }
  };

  const handleDeleteModifierTemplate = async (id) => {
    try { await axios.delete(`${API}/modifier-templates/${id}`); toast.success("Deleted"); fetchData(); }
    catch (error) { toast.error("Failed"); }
  };

  // Assign a modifier template to a menu item
  const handleAssignModifier = async (menuItemId, template) => {
    const item = menuItems.find(i => i.id === menuItemId);
    if (!item) return;
    const existing = item.modifier_groups || [];
    const alreadyAssigned = existing.some(g => g.group_name === template.group_name);
    if (alreadyAssigned) { toast.error("Already assigned"); return; }
    const updated = [...existing, { group_name: template.group_name, required: template.required, min_select: template.min_select || 0, max_select: template.max_select, options: template.options }];
    try {
      await axios.put(`${API}/menu/${menuItemId}`, { modifier_groups: updated });
      toast.success(`${template.group_name} assigned`);
      fetchData();
    } catch (error) { toast.error("Failed"); }
  };

  // ========== USER MANAGEMENT ==========
  const handleCreateUser = async () => {
    if (!newUser.pin || newUser.pin.length !== 4) { toast.error("PIN must be 4 digits"); return; }
    try {
      const payload = { ...newUser, hourly_rate: parseFloat(newUser.hourly_rate) || 0 };
      await axios.post(`${API}/auth/register`, payload);
      toast.success("User created");
      setShowUserDialog(false);
      setNewUser({ name: "", pin: "", role: "server", email: "", hourly_rate: "" });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    const updates = {};
    if (editingUser.name) updates.name = editingUser.name;
    if (editingUser.email) updates.email = editingUser.email;
    if (editingUser.role) updates.role = editingUser.role;
    if (editingUser.newPin && editingUser.newPin.length === 4) updates.pin = editingUser.newPin;
    // Include hourly_rate in updates (allow 0 as valid value)
    if (editingUser.hourly_rate !== undefined && editingUser.hourly_rate !== null) {
      updates.hourly_rate = parseFloat(editingUser.hourly_rate) || 0;
    }
    
    try {
      await axios.put(`${API}/users/${editingUser.id}`, updates);
      toast.success("User updated");
      setEditingUser(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const handleDeleteUser = async (userId) => {
    try { await axios.delete(`${API}/users/${userId}`); toast.success("Deleted"); fetchData(); }
    catch (error) { toast.error("Failed"); }
  };

  // ========== EQUIPMENT MANAGEMENT ==========
  const EQUIP_TYPES = [
    { value: "printer", label: "Printer" }, { value: "tablet", label: "Tablet" },
    { value: "handheld", label: "Handheld" }, { value: "cash_drawer", label: "Cash Drawer" },
    { value: "scanner", label: "Scanner" }, { value: "kds", label: "KDS" },
    { value: "workstation", label: "Workstation" }, { value: "customer_display", label: "Customer Display" },
  ];
  const handleSaveEquip = async () => {
    if (!equipForm.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editingEquip) { await axios.put(`${API}/equipment/${editingEquip.id}`, equipForm); toast.success("Updated"); }
      else { await axios.post(`${API}/equipment`, equipForm); toast.success("Device added"); }
      setShowEquipDialog(false); setEditingEquip(null);
      setEquipForm({ name: "", type: "printer", model_name: "", serial_number: "", ip_address: "", mac_address: "", connection_type: "network", device_path: "", location: "", assigned_station: "", assigned_printer: "", status: "offline", notes: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const handleDeleteEquip = async (id) => {
    try { await axios.delete(`${API}/equipment/${id}`); toast.success("Deleted"); fetchData(); }
    catch (err) { toast.error("Failed"); }
  };

  // ========== NETWORK MANAGEMENT ==========
  const handleSaveNet = async () => {
    if (!netForm.name.trim() || !netForm.ssid.trim()) { toast.error("Name and SSID required"); return; }
    try {
      if (editingNet) { await axios.put(`${API}/network/${editingNet.id}`, netForm); toast.success("Updated"); }
      else { await axios.post(`${API}/network`, netForm); toast.success("Network added"); }
      setShowNetDialog(false); setEditingNet(null);
      setNetForm({ name: "", ssid: "", password: "", security_type: "WPA2", ip_range: "", subnet_mask: "255.255.255.0", gateway: "", dns_primary: "", dns_secondary: "", vlan_id: "", purpose: "", notes: "" });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const handleDeleteNet = async (id) => {
    try { await axios.delete(`${API}/network/${id}`); toast.success("Deleted"); fetchData(); }
    catch (err) { toast.error("Failed"); }
  };

  const handleScanWifi = async () => {
    setScanLoading(true);
    setScanResults(null);
    try {
      const res = await axios.get(`${API}/network/scan`);
      // Sort by signal desc
      const sorted = [...(res.data?.networks || [])].sort((a, b) => (b.signal || 0) - (a.signal || 0));
      setScanResults({ ...res.data, networks: sorted });
      if (res.data?.mocked) {
        toast.info("Showing sample networks (cloud server can't see local Wi-Fi)");
      } else {
        toast.success(`Found ${sorted.length} networks via ${res.data.source}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Wi-Fi scan failed");
    } finally {
      setScanLoading(false);
    }
  };

  const handleSelectScannedNetwork = (net) => {
    // Pre-fill the Add Network dialog with the SSID + security from the scan
    setEditingNet(null);
    setNetForm({
      name: net.ssid,
      ssid: net.ssid,
      password: "",
      security_type: (net.security && net.security.toLowerCase() !== "open") ? net.security : "Open",
      ip_range: "",
      subnet_mask: "255.255.255.0",
      gateway: "",
      dns_primary: "",
      dns_secondary: "",
      vlan_id: "",
      purpose: "",
      notes: scanResults?.mocked ? "Sample network (mock scan)" : `Discovered via Wi-Fi scan (${scanResults?.source})`,
    });
    setShowNetDialog(true);
  };

  const isAdmin = user?.role === "admin";

  // Store settings save
  const handleSaveStoreSettings = async (updates) => {
    try {
      const res = await axios.put(`${API}/store-settings`, updates);
      setStoreSettings(res.data);
      toast.success("Settings saved");
      // Broadcast so useSiteBranding refetches — keeps favicon and tab
      // title in sync after logo / name / website-title edits.
      if (
        "logo_url" in updates ||
        "company_name" in updates ||
        "website_title" in updates
      ) {
        window.dispatchEvent(new Event("crest:branding-updated"));
      }
    } catch (err) { toast.error("Failed to save settings"); }
  };

  // Logo-crop modal — opens after the operator picks a file so they can
  // reposition/rotate/zoom before we persist. On save we still run the
  // result through ``autoPadLogo`` so edge-to-edge crops don't touch the
  // muted surround.
  const [cropSource, setCropSource] = useState(null);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    // Reset the input so re-uploading the same file re-triggers.
    if (e.target) e.target.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setCropSource(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleCroppedLogo = async (croppedDataUrl) => {
    setCropSource(null);
    let dataUrl = croppedDataUrl;
    try {
      dataUrl = await autoPadLogo(croppedDataUrl);
      if (dataUrl !== croppedDataUrl) {
        toast.success("Logo padded automatically for better framing");
      }
    } catch { /* fall back to raw */ }
    setStoreSettings(prev => ({ ...prev, logo_url: dataUrl }));
    handleSaveStoreSettings({ logo_url: dataUrl });
  };
  const roomTables = tables.filter(t => t.room === selectedRoom);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="min-h-screen bg-background" data-testid="settings-page">
      <Sidebar />
      <main className="main-content p-6">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-medium">Settings</h1>
          <p className="text-muted-foreground">Manage your restaurant configuration</p>
        </div>

        {(() => {
          // Two-level nav: pick a category on top, then a sub-tab under it.
          // Definitions kept adjacent to the render so adding a new sub-tab
          // (e.g. Reports) is a one-line append.
          const CATEGORIES = [
            {
              key: "restaurant",
              label: "Company / Restaurant",
              icon: <Buildings size={16} />,
              subs: [
                { value: "company", label: "Company", icon: <Buildings size={16} />, adminOnly: false, testid: "company-tab" },
                { value: "layout", label: "Floor Layout", icon: <SquaresFour size={16} />, adminOnly: false, testid: null },
                { value: "loyalty", label: "Loyalty", icon: <Crown size={16} />, adminOnly: true, testid: "loyalty-tab" },
                { value: "tax", label: "Tax", icon: <Percent size={16} />, adminOnly: false, testid: "tax-tab" },
                { value: "tip-pool", label: "Tip Pool", icon: <Percent size={16} />, adminOnly: true, testid: "tip-pool-tab" },
                { value: "online-ordering", label: "Online Ordering", icon: <QrCode size={16} />, adminOnly: true, testid: "online-ordering-tab" },
                { value: "notifications", label: "Notifications", icon: <Bell size={16} />, adminOnly: true, testid: "notifications-tab" },
              ],
            },
            {
              key: "menu",
              label: "Menu",
              icon: <ForkKnife size={16} />,
              subs: [
                { value: "menu", label: "Menu", icon: <ForkKnife size={16} />, adminOnly: false, testid: null },
                { value: "modifiers", label: "Modifiers", icon: <Sliders size={16} />, adminOnly: false, testid: "modifiers-tab" },
                { value: "gift-cards", label: "Gift Cards", icon: <CreditCard size={16} />, adminOnly: false, testid: "gift-cards-tab" },
              ],
            },
            {
              key: "equipment",
              label: "Equipment",
              icon: <Desktop size={16} />,
              subs: [
                { value: "equipment", label: "Equipment", icon: <Desktop size={16} />, adminOnly: false, testid: "equipment-tab" },
                { value: "network", label: "Network", icon: <WifiHigh size={16} />, adminOnly: false, testid: "network-tab" },
                { value: "payment", label: "Payment", icon: <CreditCard size={16} />, adminOnly: false, testid: null },
                { value: "qr-codes", label: "QR Ordering", icon: <QrCode size={16} />, adminOnly: false, testid: "qr-codes-tab" },
                { value: "pos-settings", label: "POS", icon: <SquaresFour size={16} />, adminOnly: false, testid: "pos-settings-tab" },
                { value: "database", label: "Database", icon: <Database size={16} />, adminOnly: true, testid: "database-tab" },
              ],
            },
            {
              key: "delivery",
              label: "Delivery",
              icon: <Truck size={16} />,
              subs: [
                { value: "delivery-settings", label: "Settings", icon: <Sliders size={16} />, adminOnly: false, testid: "delivery-settings-tab" },
                { value: "delivery-menu", label: "Menu", icon: <ForkKnife size={16} />, adminOnly: false, testid: "delivery-menu-tab" },
                { value: "delivery-heatmap", label: "Heatmap", icon: <MapPin size={16} />, adminOnly: false, testid: "delivery-heatmap-tab" },
                { value: "delivery-doordash", label: "DoorDash", icon: <Truck size={16} />, adminOnly: true, testid: "delivery-doordash-tab" },
                { value: "delivery-ubereats", label: "Uber Eats", icon: <Truck size={16} />, adminOnly: true, testid: "delivery-ubereats-tab" },
                { value: "delivery-grubhub", label: "Grubhub", icon: <Truck size={16} />, adminOnly: true, testid: "delivery-grubhub-tab" },
              ],
            },
          ];

          // Filter category subs by admin-only visibility.
          const visibleCats = CATEGORIES.map((c) => ({
            ...c,
            subs: c.subs.filter((s) => !s.adminOnly || isAdmin),
          })).filter((c) => c.subs.length > 0);

          const activeCat = visibleCats.find((c) => c.key === settingsCategory) || visibleCats[0];

          return (
            <Tabs value={settingsSubTab} onValueChange={setSettingsSubTab} className="space-y-4">
              {/* Level 1 — category pills */}
              <div className="flex flex-wrap gap-2" data-testid="settings-categories">
                {visibleCats.map((c) => {
                  const isActive = c.key === activeCat?.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => {
                        setSettingsCategory(c.key);
                        // Jump to first sub-tab in the newly selected category
                        // if the current one doesn't belong here.
                        if (!c.subs.find((s) => s.value === settingsSubTab)) {
                          setSettingsSubTab(c.subs[0].value);
                        }
                      }}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                      }`}
                      data-testid={`settings-category-${c.key}`}
                    >
                      {c.icon}
                      {c.label}
                    </button>
                  );
                })}
              </div>

              {/* Level 2 — sub-tab strip for the active category */}
              <TabsList className="bg-secondary/60 flex-wrap" data-testid="settings-subtabs">
                {(activeCat?.subs || []).map((s) => (
                  <TabsTrigger
                    key={s.value}
                    value={s.value}
                    className="gap-2"
                    {...(s.testid ? { "data-testid": s.testid } : {})}
                  >
                    {s.icon}
                    {s.label}
                  </TabsTrigger>
                ))}
              </TabsList>

          {/* ========== FLOOR LAYOUT TAB ========== */}
          <TabsContent value="layout">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Floor Layout Editor</CardTitle>
                  <CardDescription>Drag tables to rearrange. {isAdmin ? "Admin only." : ""}</CardDescription>
                </div>
                <div className="flex gap-2">
                  {isAdmin && (
                    <Button variant="outline" onClick={() => setShowRoomDialog(true)} className="gap-2" data-testid="add-room-btn">
                      <Plus size={16} /> Room
                    </Button>
                  )}
                  <Button onClick={() => { setNewTable(n => ({ ...n, room: selectedRoom })); setShowTableDialog(true); }} className="gap-2" data-testid="add-table-btn">
                    <Plus size={18} /> Table
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setSettingsCategory("equipment"); setSettingsSubTab("qr-codes"); }}
                    className="gap-2"
                    data-testid="layout-qr-codes-btn"
                    title="Print per-table QR codes"
                  >
                    <QrCode size={16} /> QR Codes
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Room Tabs — admin can drag to reorder, anyone can click to select */}
                <Reorder.Group
                  axis="x"
                  values={rooms}
                  onReorder={handleReorderRooms}
                  className="flex items-center gap-2 mb-4 flex-wrap"
                  as="div"
                >
                  {rooms.map(room => (
                    <Reorder.Item
                      key={room.id}
                      value={room}
                      as="div"
                      className="flex items-center gap-1"
                      dragListener={isAdmin}
                      whileDrag={{ scale: 1.04, zIndex: 50, cursor: "grabbing" }}
                      data-testid={`room-reorder-${room.id}`}
                    >
                      <Button
                        variant={selectedRoom === room.name ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedRoom(room.name)}
                        className="gap-1.5"
                        data-testid={`settings-room-${room.name}`}
                      >
                        {isAdmin && (
                          <DotsSixVertical
                            size={14}
                            className="opacity-50 cursor-grab active:cursor-grabbing"
                            data-testid={`room-drag-handle-${room.id}`}
                          />
                        )}
                        {room.name} ({tables.filter(t => t.room === room.name).length})
                      </Button>
                      {isAdmin && room.name !== "Main Floor" && (
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => handleDeleteRoom(room.id)} data-testid={`delete-room-${room.id}`}>
                          <Trash size={12} />
                        </Button>
                      )}
                    </Reorder.Item>
                  ))}
                </Reorder.Group>

                {/* Draggable Floor Plan */}
                <DraggableFloorEditor
                  tables={roomTables}
                  onDrag={handleTableDrag}
                  onDelete={handleDeleteTable}
                  onUpdate={async (tableId, updates) => {
                    try {
                      await axios.put(`${API}/tables/${tableId}`, updates);
                      setTables((prev) => prev.map((t) => (t.id === tableId ? { ...t, ...updates } : t)));
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Failed to update table");
                    }
                  }}
                  isAdmin={isAdmin}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== MENU TAB ========== */}
          <TabsContent value="menu">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div><CardTitle>Menu Items</CardTitle><CardDescription>Alphabetized by default within each category. Drag the handle to lock a custom order.</CardDescription></div>
                <div className="flex items-center gap-2">
                  <BulkAvailabilityButton onDone={fetchData} />
                  <MenuCsvImport onImportComplete={fetchData} />
                  <Button
                    className="gap-2"
                    variant="outline"
                    onClick={async () => {
                      const name = window.prompt("New category name (e.g. Pizza, Wines)");
                      if (!name || !name.trim()) return;
                      try {
                        await axios.post(`${API}/menu-categories`, { name: name.trim() });
                        toast.success(`Added category "${name.trim()}"`);
                        fetchData();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Couldn't add category");
                      }
                    }}
                    data-testid="add-menu-category-btn"
                  >
                    <Plus size={18} /> Add Category
                  </Button>
                  <Button className="gap-2" onClick={() => setShowMenuDialog(true)} data-testid="add-menu-item-btn"><Plus size={18} /> Add Item</Button>
                </div>
              </CardHeader>
              <CardContent>
                <MenuItemsReorder
                  items={menuItems}
                  categories={menuCategories}
                  cashDiscountPercentage={storeSettings?.cash_discount_percentage || 0}
                  onReorder={async (categoryId, orderedIds) => {
                    // Optimistic update — merge new sort_order into local
                    // state so the drop feels instant, then persist.
                    const orderMap = Object.fromEntries(orderedIds.map((id, i) => [id, i]));
                    setMenuItems((prev) => prev.map((it) => (
                      it.category === categoryId && it.id in orderMap
                        ? { ...it, sort_order: orderMap[it.id] }
                        : it
                    )));
                    try {
                      await axios.put(`${API}/menu/reorder`, { item_ids: orderedIds });
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Failed to save menu order");
                    }
                  }}
                  onEdit={(item) => setEditingMenu({ ...item, price: item.price })}
                  onDelete={handleDeleteMenuItem}
                  onToggleAvailability={handleToggleAvailability}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== MODIFIERS TAB ========== */}
          <TabsContent value="modifiers">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Modifier Groups</CardTitle>
                  <CardDescription>Create reusable modifier groups and assign them to menu items</CardDescription>
                </div>
                <Button className="gap-2" onClick={() => setShowModifierDialog(true)} data-testid="add-modifier-group-btn">
                  <Plus size={18} /> New Group
                </Button>
              </CardHeader>
              <CardContent>
                {modifierTemplates.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Sliders size={48} className="mx-auto mb-2 opacity-30" />
                    <p>No modifier groups yet</p>
                    <p className="text-sm">Create groups like "Size", "Extras", "Cooking Preference"</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {modifierTemplates.map(tmpl => (
                      <div key={tmpl.id} className="bg-secondary/30 rounded-xl border border-border p-4" data-testid={`mod-tmpl-${tmpl.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h3 className="font-heading font-medium">{tmpl.group_name}</h3>
                            {tmpl.required && <Badge variant="outline" className="text-[10px] text-destructive border-destructive/30">Required</Badge>}
                            {tmpl.max_select === 1 && <Badge variant="outline" className="text-[10px]">Pick 1</Badge>}
                            {tmpl.max_select > 1 && <Badge variant="outline" className="text-[10px]">Max {tmpl.max_select}</Badge>}
                            {tmpl.max_select === 0 && <Badge variant="outline" className="text-[10px]">Unlimited</Badge>}
                          </div>
                          <div className="flex gap-1">
                            <Button variant="outline" size="sm" onClick={() => setEditingModifier({ ...tmpl })} data-testid={`edit-mod-${tmpl.id}`}>
                              <PencilSimple size={14} className="mr-1" /> Edit
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteModifierTemplate(tmpl.id)} data-testid={`del-mod-${tmpl.id}`}>
                              <Trash size={14} />
                            </Button>
                          </div>
                        </div>
                        {/* Options with pricing */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {(tmpl.options || []).map((opt, i) => (
                            <div key={i} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-border text-sm">
                              <span>{opt.name}</span>
                              <span className="font-mono text-xs text-muted-foreground">
                                {opt.price > 0 ? `+$${opt.price.toFixed(2)}` : "Free"}
                              </span>
                            </div>
                          ))}
                        </div>
                        {/* Assigned to items count */}
                        <p className="text-xs text-muted-foreground mt-2">
                          Assigned to {menuItems.filter(mi => (mi.modifier_groups || []).some(g => g.group_name === tmpl.group_name)).length} menu item(s)
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== EQUIPMENT TAB ========== */}
          <TabsContent value="equipment">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div><CardTitle>Equipment</CardTitle><CardDescription>Manage POS hardware devices and peripherals</CardDescription></div>
                <Button className="gap-2" onClick={() => { setEditingEquip(null); setEquipForm({ name: "", type: "printer", model_name: "", serial_number: "", ip_address: "", mac_address: "", connection_type: "network", device_path: "", webusb_vendor_id: null, webusb_product_id: null, webusb_serial_number: "", webusb_display_name: "", location: "", assigned_station: "", status: "offline", notes: "" }); setShowEquipDialog(true); }} data-testid="add-equipment-btn"><Plus size={18} /> Add Device</Button>
              </CardHeader>
              <CardContent>
                {equipment.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground"><Desktop size={48} className="mx-auto mb-2 opacity-30" /><p>No devices configured</p></div>
                ) : (
                  <div className="space-y-2">
                    {equipment.map(item => (
                      <div key={item.id} className="bg-secondary/50 rounded-lg p-4 flex items-center justify-between" data-testid={`equipment-${item.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="font-medium">{item.name}</p>
                            <Badge variant="outline" className="text-xs">{EQUIP_TYPES.find(t => t.value === item.type)?.label || item.type}</Badge>
                            {item.type === "printer" && defaultPrinterId === item.id && (
                              <Badge className="text-xs gap-1 bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" data-testid={`default-badge-${item.id}`}>
                                <Star size={12} weight="fill" /> Default here
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                            {item.connection_type === "usb" && item.device_path ? (
                              <span className="font-mono">{item.device_path}</span>
                            ) : item.ip_address && (
                              <span className="font-mono">{item.ip_address}</span>
                            )}
                            {item.mac_address && <span className="font-mono">{item.mac_address}</span>}
                            {item.model_name && <span>{item.model_name}</span>}
                            {item.location && <span>{item.location}</span>}
                            {item.assigned_printer && ["workstation", "tablet", "handheld"].includes(item.type) && (
                              <span className="flex items-center gap-1">Printer: {equipment.find(e => e.id === item.assigned_printer)?.name || item.assigned_printer}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="flex items-center gap-1.5">
                            <div className={`w-2 h-2 rounded-full ${item.status === "online" ? "bg-green-500" : item.status === "maintenance" ? "bg-orange-500" : "bg-gray-400"}`} />
                            <span className="text-xs capitalize">{item.status}</span>
                          </div>
                          {item.type === "printer" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => toggleDefaultPrinter(item.id)}
                              title={defaultPrinterId === item.id ? "Clear default for this terminal" : "Make default receipt printer for this terminal"}
                              className={defaultPrinterId === item.id ? "text-amber-500" : "text-muted-foreground"}
                              data-testid={`set-default-printer-${item.id}`}
                            >
                              <Star size={16} weight={defaultPrinterId === item.id ? "fill" : "regular"} />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => { setEditingEquip(item); setEquipForm({ name: item.name || "", type: item.type || "printer", model_name: item.model_name || "", serial_number: item.serial_number || "", ip_address: item.ip_address || "", mac_address: item.mac_address || "", connection_type: item.connection_type || "network", device_path: item.device_path || "", webusb_vendor_id: item.webusb_vendor_id || null, webusb_product_id: item.webusb_product_id || null, webusb_serial_number: item.webusb_serial_number || "", webusb_display_name: item.webusb_display_name || "", location: item.location || "", assigned_station: item.assigned_station || "", assigned_printer: item.assigned_printer || "", status: item.status || "offline", notes: item.notes || "" }); setShowEquipDialog(true); }} data-testid={`edit-equipment-${item.id}`}><PencilSimple size={16} /></Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteEquip(item.id)} data-testid={`delete-equipment-${item.id}`}><Trash size={16} /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ---- Receipt & Kitchen Ticket Options ---- */}
            <Card className="mt-4" data-testid="receipt-options-card">
              <CardHeader>
                <CardTitle>Receipt & Kitchen Ticket</CardTitle>
                <CardDescription>Control what gets printed and how big the type is.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Logo toggle */}
                <label className="flex items-center justify-between gap-4 cursor-pointer p-3 border border-border rounded-xl">
                  <div className="min-w-0">
                    <p className="font-medium text-sm">Print logo on customer receipts</p>
                    <p className="text-xs text-muted-foreground">
                      When on, browser-printed receipts show your logo at the top. Uses the logo from Settings → Company.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    className="h-5 w-5 accent-primary"
                    checked={!!storeSettings.receipt_print_logo}
                    onChange={(e) => {
                      setStoreSettings({ ...storeSettings, receipt_print_logo: e.target.checked });
                      handleSaveStoreSettings({ receipt_print_logo: e.target.checked });
                    }}
                    data-testid="receipt-print-logo-toggle"
                  />
                </label>

                {/* Receipt font scale */}
                <div className="p-3 border border-border rounded-xl">
                  <p className="font-medium text-sm mb-1">Receipt font size</p>
                  <p className="text-xs text-muted-foreground mb-2">
                    Sets the size of the printed customer receipt (after payment).
                  </p>
                  <div className="flex gap-2" data-testid="receipt-font-scale-group">
                    {["small", "normal", "large"].map((v) => (
                      <Button
                        key={v}
                        variant={(storeSettings.receipt_font_scale || "normal") === v ? "default" : "outline"}
                        size="sm"
                        className="capitalize"
                        onClick={() => {
                          setStoreSettings({ ...storeSettings, receipt_font_scale: v });
                          handleSaveStoreSettings({ receipt_font_scale: v });
                        }}
                        data-testid={`receipt-font-scale-${v}`}
                      >{v}</Button>
                    ))}
                  </div>
                </div>

                {/* Kitchen ticket font scale */}
                <div className="p-3 border border-border rounded-xl">
                  <p className="font-medium text-sm mb-1">Kitchen ticket font size</p>
                  <p className="text-xs text-muted-foreground mb-2">
                    Sets the size of the ticket shown on the Kitchen Display when an order is sent from the POS. KDS operators may need to refresh once after a change.
                  </p>
                  <div className="flex gap-2" data-testid="kitchen-font-scale-group">
                    {["small", "normal", "large"].map((v) => (
                      <Button
                        key={v}
                        variant={(storeSettings.kitchen_ticket_font_scale || "normal") === v ? "default" : "outline"}
                        size="sm"
                        className="capitalize"
                        onClick={() => {
                          setStoreSettings({ ...storeSettings, kitchen_ticket_font_scale: v });
                          handleSaveStoreSettings({ kitchen_ticket_font_scale: v });
                        }}
                        data-testid={`kitchen-font-scale-${v}`}
                      >{v}</Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="network">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div><CardTitle>Network</CardTitle><CardDescription>Configure WiFi networks for device communication</CardDescription></div>
                <div className="flex items-center gap-2">
                  {isAdmin && <Button variant="outline" className="gap-2" onClick={handleScanWifi} disabled={scanLoading} data-testid="scan-wifi-btn"><WifiHigh size={18} /> {scanLoading ? "Scanning..." : "Scan for Networks"}</Button>}
                  {isAdmin && <Button className="gap-2" onClick={() => { setEditingNet(null); setNetForm({ name: "", ssid: "", password: "", security_type: "WPA2", ip_range: "", subnet_mask: "255.255.255.0", gateway: "", dns_primary: "", dns_secondary: "", vlan_id: "", purpose: "", notes: "" }); setShowNetDialog(true); }} data-testid="add-network-btn"><Plus size={18} /> Add Network</Button>}
                </div>
              </CardHeader>
              <CardContent>
                {/* Scan Results */}
                {(scanLoading || scanResults) && (
                  <div className="mb-6 bg-secondary/30 rounded-xl border border-border p-4" data-testid="wifi-scan-results">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-sm">
                        Available Networks
                        {scanResults?.mocked && (
                          <Badge variant="outline" className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 border-amber-300 bg-amber-50">MOCKED</Badge>
                        )}
                      </h4>
                      <Button variant="ghost" size="sm" onClick={() => setScanResults(null)} className="h-7 text-xs" data-testid="scan-close-btn">Close</Button>
                    </div>
                    {scanResults?.mocked && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 mb-3">
                        This server can't see local Wi-Fi (running in cloud). Showing sample results so you can test the flow. When deployed on an on-premise POS box with Wi-Fi, this list will be real.
                      </p>
                    )}
                    {scanLoading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        Scanning for networks...
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {(scanResults?.networks || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground py-3">No networks found.</p>
                        ) : (
                          scanResults.networks.map((net, idx) => (
                            <div key={`${net.ssid}-${idx}`} className="flex items-center justify-between bg-white rounded-lg border border-border px-3 py-2" data-testid={`scan-net-${idx}`}>
                              <div className="flex items-center gap-3 min-w-0">
                                <WifiHigh size={18} className={net.signal >= 70 ? "text-green-600" : net.signal >= 40 ? "text-amber-500" : "text-muted-foreground"} weight={net.signal >= 70 ? "fill" : "regular"} />
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate">{net.ssid}</p>
                                  <p className="text-[11px] text-muted-foreground">{net.security || "Open"} · Signal {net.signal}%</p>
                                </div>
                              </div>
                              <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => handleSelectScannedNetwork(net)} data-testid={`scan-connect-${idx}`}>Connect</Button>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                {networks.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground"><WifiHigh size={48} className="mx-auto mb-2 opacity-30" /><p>No networks configured</p></div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {networks.map(net => (
                      <div key={net.id} className="bg-secondary/50 rounded-lg p-4" data-testid={`network-${net.id}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-medium">{net.name}</p>
                            <p className="text-xs font-mono text-muted-foreground">{net.ssid}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            {isAdmin && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingNet(net); setNetForm({ name: net.name || "", ssid: net.ssid || "", password: net.password || "", security_type: net.security_type || "WPA2", ip_range: net.ip_range || "", subnet_mask: net.subnet_mask || "255.255.255.0", gateway: net.gateway || "", dns_primary: net.dns_primary || "", dns_secondary: net.dns_secondary || "", vlan_id: net.vlan_id || "", purpose: net.purpose || "", notes: net.notes || "" }); setShowNetDialog(true); }} data-testid={`edit-network-${net.id}`}><PencilSimple size={14} /></Button>}
                            {isAdmin && <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteNet(net.id)} data-testid={`delete-network-${net.id}`}><Trash size={14} /></Button>}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                          <div><span className="text-muted-foreground text-xs">Security</span><p className="text-xs font-medium">{net.security_type}</p></div>
                          <div><span className="text-muted-foreground text-xs">Password</span><p className="text-xs font-mono">{showNetPasswords[net.id] ? (net.password || "—") : "********"} <button onClick={() => setShowNetPasswords(p => ({...p, [net.id]: !p[net.id]}))} className="text-muted-foreground hover:text-foreground ml-1 text-[10px]" data-testid={`toggle-pass-${net.id}`}>{showNetPasswords[net.id] ? "hide" : "show"}</button></p></div>
                          {net.ip_range && <div><span className="text-muted-foreground text-xs">IP Range</span><p className="text-xs font-mono">{net.ip_range}</p></div>}
                          {net.gateway && <div><span className="text-muted-foreground text-xs">Gateway</span><p className="text-xs font-mono">{net.gateway}</p></div>}
                          {net.vlan_id && <div><span className="text-muted-foreground text-xs">VLAN</span><p className="text-xs font-mono">{net.vlan_id}</p></div>}
                          {net.dns_primary && <div><span className="text-muted-foreground text-xs">DNS</span><p className="text-xs font-mono">{net.dns_primary}</p></div>}
                        </div>
                        {net.purpose && <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">{net.purpose}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== TAX TAB ========== */}
          <TabsContent value="tax">
            <Card>
              <CardHeader><CardTitle>Tax Rate</CardTitle><CardDescription>Set the sales tax percentage applied to all orders</CardDescription></CardHeader>
              <CardContent>
                <div className="max-w-xs space-y-4">
                  <div className="space-y-2">
                    <Label>Tax Percentage (%)</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={storeSettings.tax_rate ?? 8.0}
                        onChange={(e) => setStoreSettings({ ...storeSettings, tax_rate: parseFloat(e.target.value) || 0 })}
                        className="font-mono text-lg w-32"
                        data-testid="tax-rate-input"
                      />
                      <span className="text-2xl text-muted-foreground">%</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Current: {storeSettings.tax_rate}% tax on all orders</p>
                  </div>
                  <Button onClick={() => handleSaveStoreSettings({ tax_rate: storeSettings.tax_rate })} data-testid="save-tax-btn">
                    Save Tax Rate
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== ONLINE ORDERING TAB ========== */}
          <TabsContent value="online-ordering">
            <Card>
              <CardHeader>
                <CardTitle>Public Online Ordering</CardTitle>
                <CardDescription>
                  Enable pickup and delivery ordering from the /order landing page — no table QR needed.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-start gap-3 p-4 rounded-lg border cursor-pointer hover:bg-secondary/30" data-testid="toggle-pickup-wrap">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4"
                      checked={!!storeSettings.online_pickup_enabled}
                      onChange={(e) => setStoreSettings({ ...storeSettings, online_pickup_enabled: e.target.checked })}
                      data-testid="toggle-pickup"
                    />
                    <div>
                      <p className="font-medium">Enable Pickup Orders</p>
                      <p className="text-xs text-muted-foreground">Customers can order online and pick up at the counter.</p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-4 rounded-lg border cursor-pointer hover:bg-secondary/30" data-testid="toggle-delivery-wrap">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4"
                      checked={!!storeSettings.online_delivery_enabled}
                      onChange={(e) => setStoreSettings({ ...storeSettings, online_delivery_enabled: e.target.checked })}
                      data-testid="toggle-delivery"
                    />
                    <div>
                      <p className="font-medium">Enable Delivery Orders</p>
                      <p className="text-xs text-muted-foreground">Customers submit a delivery address; you dispatch a driver.</p>
                    </div>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Delivery Fee ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={storeSettings.delivery_fee ?? 0}
                      onChange={(e) => setStoreSettings({ ...storeSettings, delivery_fee: parseFloat(e.target.value) || 0 })}
                      className="font-mono"
                      data-testid="delivery-fee-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Delivery Minimum Order ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={storeSettings.delivery_min_order ?? 0}
                      onChange={(e) => setStoreSettings({ ...storeSettings, delivery_min_order: parseFloat(e.target.value) || 0 })}
                      className="font-mono"
                      data-testid="delivery-min-order-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Pickup Ready Estimate</Label>
                    <Input
                      value={storeSettings.pickup_ready_estimate || ""}
                      onChange={(e) => setStoreSettings({ ...storeSettings, pickup_ready_estimate: e.target.value })}
                      placeholder="e.g. 20-30 minutes"
                      data-testid="pickup-estimate-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Delivery Ready Estimate</Label>
                    <Input
                      value={storeSettings.delivery_ready_estimate || ""}
                      onChange={(e) => setStoreSettings({ ...storeSettings, delivery_ready_estimate: e.target.value })}
                      placeholder="e.g. 45-60 minutes"
                      data-testid="delivery-estimate-input"
                    />
                  </div>
                </div>

                <Button
                  onClick={() => handleSaveStoreSettings({
                    online_pickup_enabled: !!storeSettings.online_pickup_enabled,
                    online_delivery_enabled: !!storeSettings.online_delivery_enabled,
                    delivery_fee: parseFloat(storeSettings.delivery_fee) || 0,
                    delivery_min_order: parseFloat(storeSettings.delivery_min_order) || 0,
                    pickup_ready_estimate: storeSettings.pickup_ready_estimate || "",
                    delivery_ready_estimate: storeSettings.delivery_ready_estimate || "",
                  })}
                  data-testid="save-online-ordering-btn"
                >
                  Save Online Ordering Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== NOTIFICATIONS TAB ========== */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Order Notifications (SMS + Email)</CardTitle>
                <CardDescription>
                  Configure Twilio (SMS) and SendGrid (email) to auto-send order confirmations and ready alerts to customers.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Status badges */}
                <div className="flex flex-wrap gap-2 text-xs">
                  <Badge variant={notifConfig?.sms_configured ? "default" : "secondary"} data-testid="sms-status-badge">
                    <ChatCircle size={12} className="mr-1" /> SMS {notifConfig?.sms_configured ? "configured" : "not configured"}
                  </Badge>
                  <Badge variant={notifConfig?.email_configured ? "default" : "secondary"} data-testid="email-status-badge">
                    <PaperPlaneTilt size={12} className="mr-1" /> Email {notifConfig?.email_configured ? "configured" : "not configured"}
                  </Badge>
                </div>

                {/* SMS Cost Meter (month-to-date) */}
                {smsUsage && (
                  <div
                    className="rounded-lg border bg-secondary/20 p-4 flex flex-wrap items-center gap-6"
                    data-testid="sms-cost-meter"
                  >
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                        SMS Spend · {smsUsage.period_label}
                      </div>
                      <div className="text-2xl font-semibold tabular-nums" data-testid="sms-spend-value">
                        ${smsUsage.estimated_spend_usd.toFixed(2)}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground border-l pl-6 space-y-0.5">
                      <div>
                        <b data-testid="sms-messages-count">{smsUsage.messages}</b> messages ·{" "}
                        <b data-testid="sms-segments-count">{smsUsage.segments}</b> segments
                      </div>
                      <div>
                        Rate: ${smsUsage.rate_usd.toFixed(4)}/segment · resets 1st of month
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto text-xs"
                      onClick={loadSmsUsage}
                      data-testid="sms-usage-refresh"
                    >
                      Refresh
                    </Button>
                  </div>
                )}

                {/* Twilio (SMS) */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <ChatCircle size={18} className="text-primary" />
                    <h3 className="font-medium">Twilio (SMS)</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Account SID</Label>
                      <Input
                        value={notifDraft.twilio_account_sid || ""}
                        onChange={(e) => setNotifDraft({ ...notifDraft, twilio_account_sid: e.target.value })}
                        placeholder="AC..."
                        className="font-mono text-xs"
                        data-testid="twilio-sid-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>Auth Token</Label>
                      <Input
                        type="password"
                        value={notifDraft.twilio_auth_token || ""}
                        onChange={(e) => setNotifDraft({ ...notifDraft, twilio_auth_token: e.target.value })}
                        placeholder={notifConfig?.has_twilio_auth_token ? notifConfig?.twilio_auth_token_masked : "Enter auth token"}
                        className="font-mono text-xs"
                        data-testid="twilio-token-input"
                      />
                      <p className="text-[10px] text-muted-foreground">Leave blank to keep existing.</p>
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <Label>From Number</Label>
                      <Input
                        value={notifDraft.twilio_from_number || ""}
                        onChange={(e) => setNotifDraft({ ...notifDraft, twilio_from_number: e.target.value })}
                        placeholder="+15551234567"
                        className="font-mono"
                        data-testid="twilio-from-input"
                      />
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <Label>Price per SMS Segment (USD)</Label>
                      <Input
                        type="number"
                        step="0.0001"
                        min="0"
                        value={notifDraft.twilio_sms_rate_usd ?? 0.0079}
                        onChange={(e) => setNotifDraft({ ...notifDraft, twilio_sms_rate_usd: e.target.value === "" ? 0 : Number(e.target.value) })}
                        placeholder="0.0079"
                        className="font-mono text-xs"
                        data-testid="twilio-rate-input"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Used to estimate the month-to-date SMS spend meter. US default ≈ $0.0079/segment. Check your Twilio dashboard for country-specific rates.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* SendGrid (Email) */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <PaperPlaneTilt size={18} className="text-primary" />
                    <h3 className="font-medium">SendGrid (Email)</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>API Key</Label>
                      <Input
                        type="password"
                        value={notifDraft.sendgrid_api_key || ""}
                        onChange={(e) => setNotifDraft({ ...notifDraft, sendgrid_api_key: e.target.value })}
                        placeholder={notifConfig?.has_sendgrid_api_key ? notifConfig?.sendgrid_api_key_masked : "SG..."}
                        className="font-mono text-xs"
                        data-testid="sendgrid-key-input"
                      />
                      <p className="text-[10px] text-muted-foreground">Leave blank to keep existing.</p>
                    </div>
                    <div className="space-y-1">
                      <Label>From Email (verified sender)</Label>
                      <Input
                        type="email"
                        value={notifDraft.sendgrid_from_email || ""}
                        onChange={(e) => setNotifDraft({ ...notifDraft, sendgrid_from_email: e.target.value })}
                        placeholder="orders@yourdomain.com"
                        data-testid="sendgrid-from-input"
                      />
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <Label>From Name <span className="text-muted-foreground text-xs">(optional)</span></Label>
                      <Input
                        value={notifDraft.sendgrid_from_name || ""}
                        onChange={(e) => setNotifDraft({ ...notifDraft, sendgrid_from_name: e.target.value })}
                        placeholder={storeSettings.company_name || "Your Restaurant"}
                        data-testid="sendgrid-fromname-input"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Toggles */}
                <div className="space-y-3">
                  <h3 className="font-medium">Send Notifications For</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-secondary/30">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={!!notifDraft.order_confirmation_enabled}
                        onChange={(e) => setNotifDraft({ ...notifDraft, order_confirmation_enabled: e.target.checked })}
                        data-testid="toggle-confirmation"
                      />
                      <div>
                        <p className="font-medium text-sm">Order Confirmation</p>
                        <p className="text-xs text-muted-foreground">Sent immediately when the customer submits.</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-secondary/30">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={!!notifDraft.order_ready_enabled}
                        onChange={(e) => setNotifDraft({ ...notifDraft, order_ready_enabled: e.target.checked })}
                        data-testid="toggle-ready"
                      />
                      <div>
                        <p className="font-medium text-sm">Order Ready</p>
                        <p className="text-xs text-muted-foreground">Sent when KDS marks the order ready.</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-secondary/30">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4"
                        checked={!!notifDraft.dine_in_notifications}
                        onChange={(e) => setNotifDraft({ ...notifDraft, dine_in_notifications: e.target.checked })}
                        data-testid="toggle-dinein"
                      />
                      <div>
                        <p className="font-medium text-sm">Include Dine-in</p>
                        <p className="text-xs text-muted-foreground">By default only pickup + delivery trigger sends.</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={saveNotifConfig} data-testid="save-notifications-btn">
                    Save Notifications Settings
                  </Button>
                  <Button variant="outline" onClick={loadNotifConfig} data-testid="reload-notifications-btn">
                    Reload
                  </Button>
                </div>

                <Separator />

                {/* Review requests (fires ~N min after ready for pickup/delivery) */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Star size={18} className="text-primary" />
                    <h3 className="font-medium">Review Request Follow-up</h3>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Automatically send customers a review link a few minutes after their pickup or delivery order is marked ready.
                    Great for boosting Google reviews.
                  </p>
                  <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-secondary/30">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4"
                      checked={!!notifDraft.review_request_enabled}
                      onChange={(e) => setNotifDraft({ ...notifDraft, review_request_enabled: e.target.checked })}
                      data-testid="toggle-review-request"
                    />
                    <div>
                      <p className="font-medium text-sm">Enable review request SMS + email</p>
                      <p className="text-xs text-muted-foreground">Only applies to pickup + delivery orders with a phone or email on file.</p>
                    </div>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1 md:col-span-2">
                      <Label>Review URL</Label>
                      <Input
                        value={notifDraft.review_url || ""}
                        onChange={(e) => setNotifDraft({ ...notifDraft, review_url: e.target.value })}
                        placeholder="https://g.page/r/your-google-review-link"
                        data-testid="review-url-input"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Get this from Google Business Profile → Read reviews → Get more reviews.
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label>Delay (minutes)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="1440"
                        value={notifDraft.review_delay_minutes ?? 20}
                        onChange={(e) => setNotifDraft({ ...notifDraft, review_delay_minutes: Math.max(1, parseInt(e.target.value) || 20) })}
                        className="font-mono"
                        data-testid="review-delay-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label>Public Base URL <span className="text-muted-foreground text-xs">(for click tracking)</span></Label>
                    <Input
                      value={notifDraft.public_base_url || ""}
                      onChange={(e) => setNotifDraft({ ...notifDraft, public_base_url: e.target.value })}
                      placeholder="https://crestpos.net"
                      className="font-mono text-xs"
                      data-testid="public-base-url-input"
                    />
                    <p className="text-[10px] text-muted-foreground">
                      When set, review links go through a tracking redirect so you can measure clicks on the Feedback dashboard.
                      Leave blank to send the raw Review URL directly (no tracking).
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Test send */}
                <div className="space-y-3">
                  <h3 className="font-medium">Send Test Notification</h3>
                  <div className="flex flex-wrap gap-2 items-end">
                    <div className="space-y-1">
                      <Label>Channel</Label>
                      <select
                        value={notifTest.channel}
                        onChange={(e) => setNotifTest({ ...notifTest, channel: e.target.value })}
                        className="border rounded-md h-10 px-3 text-sm"
                        data-testid="test-channel-select"
                      >
                        <option value="sms">SMS</option>
                        <option value="email">Email</option>
                      </select>
                    </div>
                    <div className="space-y-1 flex-1 min-w-[220px]">
                      <Label>{notifTest.channel === "sms" ? "Phone (E.164)" : "Email"}</Label>
                      <Input
                        value={notifTest.to}
                        onChange={(e) => setNotifTest({ ...notifTest, to: e.target.value })}
                        placeholder={notifTest.channel === "sms" ? "+15551234567" : "you@example.com"}
                        data-testid="test-to-input"
                      />
                    </div>
                    <Button
                      onClick={sendTestNotification}
                      disabled={notifTestLoading}
                      data-testid="send-test-btn"
                    >
                      {notifTestLoading ? "Sending..." : "Send Test"}
                    </Button>
                  </div>
                </div>

                <Separator />

                {/* ========== End of Day auto-email ========== */}
                <div className="space-y-4" data-testid="eod-email-section">
                  <div className="flex items-center gap-2">
                    <PaperPlaneTilt size={18} className="text-primary" />
                    <h3 className="font-medium">End of Day Auto-Email</h3>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Each night at the time you pick, we&apos;ll email the closing report (sales, tips,
                    payment mix, top servers) and the cash-drawer log to the recipients below.
                    Requires SendGrid credentials configured above.
                  </p>

                  <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-secondary/30">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4"
                      checked={!!notifDraft.eod_email_enabled}
                      onChange={(e) => setNotifDraft({ ...notifDraft, eod_email_enabled: e.target.checked })}
                      data-testid="eod-enabled-toggle"
                    />
                    <div>
                      <p className="font-medium text-sm">Send End of Day report automatically</p>
                      <p className="text-xs text-muted-foreground">
                        Runs once per calendar day in the timezone below. You can still use &ldquo;Send Now&rdquo;
                        any time.
                      </p>
                    </div>
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="space-y-1 md:col-span-4">
                      <Label>Recipients</Label>
                      <EodRecipientChips
                        value={notifDraft.eod_email_recipients || []}
                        onChange={(next) =>
                          setNotifDraft({ ...notifDraft, eod_email_recipients: next })
                        }
                      />
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <Label>Send at (hour)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="23"
                        value={notifDraft.eod_email_hour ?? 23}
                        onChange={(e) => setNotifDraft({ ...notifDraft, eod_email_hour: e.target.value })}
                        data-testid="eod-hour-input"
                      />
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <Label>Minute</Label>
                      <Input
                        type="number"
                        min="0"
                        max="59"
                        value={notifDraft.eod_email_minute ?? 30}
                        onChange={(e) => setNotifDraft({ ...notifDraft, eod_email_minute: e.target.value })}
                        data-testid="eod-minute-input"
                      />
                    </div>
                    <div className="space-y-1 md:col-span-4">
                      <Label>Timezone (IANA — e.g. America/New_York)</Label>
                      <Input
                        value={notifDraft.eod_email_timezone ?? "UTC"}
                        onChange={(e) => setNotifDraft({ ...notifDraft, eod_email_timezone: e.target.value })}
                        placeholder="UTC"
                        data-testid="eod-tz-input"
                      />
                      <p className="text-[10px] text-muted-foreground">
                        Defaults to UTC. Use a full IANA name so we account for DST — invalid names silently fall back to UTC.
                      </p>
                    </div>
                  </div>

                  {notifConfig?.eod_email_last_sent_at && (
                    <div className="text-xs text-muted-foreground" data-testid="eod-last-sent">
                      Last automatic send:{" "}
                      <b>{notifConfig.eod_email_last_sent_date}</b>
                      {" "}({new Date(notifConfig.eod_email_last_sent_at).toLocaleString()})
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      onClick={loadEodPreview}
                      data-testid="eod-preview-btn"
                    >
                      Preview Email
                    </Button>
                    <Button
                      onClick={sendEodNow}
                      data-testid="eod-send-now-btn"
                    >
                      Send Now
                    </Button>
                  </div>

                  {eodPreview && (
                    <div
                      className="rounded-lg border overflow-hidden"
                      data-testid="eod-preview-panel"
                    >
                      <div className="px-3 py-2 bg-secondary/40 flex items-center justify-between text-xs">
                        <span>Subject: <b>{eodPreview.subject}</b></span>
                        <button
                          className="text-muted-foreground hover:text-foreground"
                          onClick={() => setEodPreview(null)}
                          data-testid="eod-preview-close"
                          aria-label="Close preview"
                        >
                          ×
                        </button>
                      </div>
                      <iframe
                        title="EOD email preview"
                        srcDoc={eodPreview.html}
                        style={{ width: "100%", height: 480, border: 0, background: "#fff" }}
                        data-testid="eod-preview-frame"
                      />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== COMPANY TAB ========== */}
          <TabsContent value="company">
            <Card>
              <CardHeader><CardTitle>Company Information</CardTitle><CardDescription>Your business details for receipts and branding</CardDescription></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Company Name</Label>
                      <Input value={storeSettings.company_name || ""} onChange={(e) => setStoreSettings({ ...storeSettings, company_name: e.target.value })} placeholder="Your Restaurant Name" data-testid="company-name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Address</Label>
                      <Input value={storeSettings.company_address || ""} onChange={(e) => setStoreSettings({ ...storeSettings, company_address: e.target.value })} placeholder="123 Main Street" data-testid="company-address" />
                    </div>
                    <div className="grid grid-cols-6 gap-3">
                      <div className="space-y-2 col-span-3">
                        <Label>City</Label>
                        <Input value={storeSettings.company_city || ""} onChange={(e) => setStoreSettings({ ...storeSettings, company_city: e.target.value })} placeholder="Anytown" data-testid="company-city" />
                      </div>
                      <div className="space-y-2 col-span-1">
                        <Label>State</Label>
                        <Input value={storeSettings.company_state || ""} onChange={(e) => setStoreSettings({ ...storeSettings, company_state: e.target.value })} placeholder="CA" maxLength={4} data-testid="company-state" />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label>Zip</Label>
                        <Input value={storeSettings.company_zip || ""} onChange={(e) => setStoreSettings({ ...storeSettings, company_zip: e.target.value })} placeholder="90210" data-testid="company-zip" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Phone Number</Label>
                      <Input value={storeSettings.company_phone || ""} onChange={(e) => setStoreSettings({ ...storeSettings, company_phone: e.target.value })} placeholder="(555) 123-4567" data-testid="company-phone" />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={storeSettings.company_email || ""} onChange={(e) => setStoreSettings({ ...storeSettings, company_email: e.target.value })} placeholder="info@restaurant.com" data-testid="company-email" />
                    </div>
                    <div className="space-y-2">
                      <Label>Website Title <span className="text-xs text-muted-foreground font-normal">(browser tab)</span></Label>
                      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3 items-start">
                        <div className="space-y-2">
                          <Input
                            value={storeSettings.website_title || ""}
                            onChange={(e) => setStoreSettings({ ...storeSettings, website_title: e.target.value })}
                            placeholder={storeSettings.company_name || "Crest POS"}
                            data-testid="company-website-title"
                          />
                          <p className="text-xs text-muted-foreground">
                            Falls back to Company Name when left blank. Also drives the login-screen heading.
                          </p>
                        </div>
                        <BrandingPreview
                          websiteTitle={storeSettings.website_title}
                          companyName={storeSettings.company_name}
                          logoUrl={storeSettings.logo_url}
                        />
                      </div>
                    </div>
                    <Button onClick={() => handleSaveStoreSettings({ company_name: storeSettings.company_name, company_address: storeSettings.company_address, company_city: storeSettings.company_city, company_state: storeSettings.company_state, company_zip: storeSettings.company_zip, company_phone: storeSettings.company_phone, company_email: storeSettings.company_email, website_title: storeSettings.website_title })} data-testid="save-company-btn">
                      Save Company Info
                    </Button>
                  </div>
                  <div className="space-y-4">
                    <Label>Company Logo</Label>
                    <div className="border-2 border-dashed border-border rounded-xl p-6 text-center" data-testid="logo-upload-area">
                      {storeSettings.logo_url ? (
                        <div className="space-y-3">
                          <img src={storeSettings.logo_url} alt="Logo" className="max-h-32 mx-auto object-contain rounded-lg" data-testid="company-logo-preview" />
                          <div className="flex items-center justify-center gap-2">
                            <label className="cursor-pointer text-sm text-primary hover:underline">
                              Change Logo
                              <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="logo-file-input" />
                            </label>
                            <button onClick={() => { setStoreSettings({ ...storeSettings, logo_url: "" }); handleSaveStoreSettings({ logo_url: "" }); }} className="text-sm text-destructive hover:underline" data-testid="remove-logo-btn">Remove</button>
                          </div>
                        </div>
                      ) : (
                        <label className="cursor-pointer block">
                          <ImageSquare size={48} className="mx-auto mb-2 text-muted-foreground opacity-30" />
                          <p className="text-sm text-muted-foreground mb-1">Click to upload logo</p>
                          <p className="text-xs text-muted-foreground">PNG, JPG up to 2MB</p>
                          <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="logo-file-input" />
                        </label>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== PAYMENT TAB ========== */}
          <TabsContent value="payment">
            <PaymentGatewayCard />
          </TabsContent>

          {/* ========== QR ORDERING TAB ========== */}
          <TabsContent value="qr-codes">
            <Tabs defaultValue="website" className="w-full">
              <TabsList data-testid="qr-ordering-subtabs">
                <TabsTrigger value="website" data-testid="qr-website-subtab">Website</TabsTrigger>
                <TabsTrigger value="tables" data-testid="qr-tables-subtab">Per-Table QR</TabsTrigger>
              </TabsList>

              <TabsContent value="website" className="mt-4 space-y-4">
                {/* Fulfillment toggles — decide whether the public /order
                    page shows the Pickup/Delivery buttons. */}
                <Card data-testid="online-fulfillment-card">
                  <CardHeader>
                    <CardTitle className="font-heading">Online Ordering Options</CardTitle>
                    <CardDescription>
                      Choose which fulfillment options guests see on the public ordering page. Turn both off and only dine-in table codes will work.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <label className="flex items-center justify-between gap-4 cursor-pointer p-3 border border-border rounded-xl">
                      <div className="min-w-0">
                        <p className="font-medium text-sm">Allow Carryout / Pickup</p>
                        <p className="text-xs text-muted-foreground">
                          Guests can place a pickup order and come collect it. Uses the ready-time estimate from Delivery settings.
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        className="h-5 w-5 accent-primary"
                        checked={!!storeSettings.online_pickup_enabled}
                        onChange={(e) => {
                          setStoreSettings({ ...storeSettings, online_pickup_enabled: e.target.checked });
                          handleSaveStoreSettings({ online_pickup_enabled: e.target.checked });
                        }}
                        data-testid="toggle-online-pickup"
                      />
                    </label>
                    <label className="flex items-center justify-between gap-4 cursor-pointer p-3 border border-border rounded-xl">
                      <div className="min-w-0">
                        <p className="font-medium text-sm">Allow Delivery</p>
                        <p className="text-xs text-muted-foreground">
                          Guests can place a delivery order. Uses the delivery fee, min-order and ready-time from Delivery settings. First-party drivers (if enabled) auto-quote a live ETA.
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        className="h-5 w-5 accent-primary"
                        checked={!!storeSettings.online_delivery_enabled}
                        onChange={(e) => {
                          setStoreSettings({ ...storeSettings, online_delivery_enabled: e.target.checked });
                          handleSaveStoreSettings({ online_delivery_enabled: e.target.checked });
                        }}
                        data-testid="toggle-online-delivery"
                      />
                    </label>
                  </CardContent>
                </Card>

                {/* Public URL + QR + share/print tools */}
                <PublicOrderLinkCard />
              </TabsContent>

              <TabsContent value="tables" className="mt-4">
                <PerTableQRCodesCard tables={tables} rooms={rooms} />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* ========== TIP POOL TAB (admin) ========== */}
          {isAdmin && (
            <TabsContent value="tip-pool">
              <TipPoolCard />
            </TabsContent>
          )}

          {/* ========== GIFT CARDS TAB (admin) ========== */}
          {isAdmin && (
            <TabsContent value="gift-cards">
              <GiftCardsCard />
            </TabsContent>
          )}

          {/* ========== LOYALTY TAB (admin only) ========== */}
          {isAdmin && (
            <TabsContent value="loyalty">
              <LoyaltyTiersCard />
            </TabsContent>
          )}

          {/* ========== POS SETTINGS TAB ========== */}
          <TabsContent value="pos-settings">
            <PosSettingsContent />
          </TabsContent>

          {/* ========== DATABASE HEALTH TAB (admin only) ========== */}
          {isAdmin && (
            <TabsContent value="database">
              <div className="space-y-6">
                <BuildInfoChip />
                <SystemHealthCard />
                <DatabaseHealthCard />
                <DataArchiveCard />
              </div>
            </TabsContent>
          )}

          <TabsContent value="delivery-settings">
            <DeliverySettings />
          </TabsContent>
          <TabsContent value="delivery-menu">
            <DeliveryMenu />
          </TabsContent>
          <TabsContent value="delivery-heatmap">
            <DeliveryHeatmap />
          </TabsContent>
          <TabsContent value="delivery-doordash">
            <div className="space-y-6">
              <AutoDispatchCard />
              <DoorDashCard />
            </div>
          </TabsContent>
          <TabsContent value="delivery-ubereats">
            <div className="space-y-6">
              <AutoDispatchCard />
              <UberEatsCard />
            </div>
          </TabsContent>
          <TabsContent value="delivery-grubhub">
            <div className="space-y-6">
              <AutoDispatchCard />
              <GrubhubCard />
            </div>
          </TabsContent>
        </Tabs>
          );
        })()}
      </main>

      {/* ========== DIALOGS ========== */}

      {/* Add Table */}
      <Dialog open={showTableDialog} onOpenChange={setShowTableDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Table</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Name</Label><Input placeholder="Table 9" value={newTable.name} onChange={(e) => setNewTable({ ...newTable, name: e.target.value })} data-testid="new-table-name" /></div>
            <div className="space-y-2"><Label>Seats</Label><Input type="number" min={1} value={newTable.seats} onChange={(e) => setNewTable({ ...newTable, seats: parseInt(e.target.value) })} data-testid="new-table-seats" /></div>
            <div className="space-y-2"><Label>Shape</Label>
              <Select value={newTable.shape} onValueChange={(v) => setNewTable({ ...newTable, shape: v })}>
                <SelectTrigger data-testid="new-table-shape"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="square">Square</SelectItem><SelectItem value="round">Round</SelectItem><SelectItem value="rectangle">Rectangle</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Room</Label>
              <Select value={newTable.room} onValueChange={(v) => setNewTable({ ...newTable, room: v })}>
                <SelectTrigger data-testid="new-table-room"><SelectValue /></SelectTrigger>
                <SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowTableDialog(false)}>Cancel</Button><Button onClick={handleCreateTable} data-testid="save-table-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Room */}
      <Dialog open={showRoomDialog} onOpenChange={setShowRoomDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Room</DialogTitle></DialogHeader>
          <div className="py-4 space-y-2"><Label>Room Name</Label><Input placeholder="VIP Lounge" value={newRoom} onChange={(e) => setNewRoom(e.target.value)} data-testid="new-room-name" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setShowRoomDialog(false)}>Cancel</Button><Button onClick={handleCreateRoom} data-testid="save-room-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Menu Item */}
      <Dialog open={showMenuDialog} onOpenChange={setShowMenuDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Menu Item</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2"><Label>Name</Label><Input value={newMenuItem.name} onChange={(e) => setNewMenuItem({ ...newMenuItem, name: e.target.value })} data-testid="new-menu-name" /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={newMenuItem.description} onChange={(e) => setNewMenuItem({ ...newMenuItem, description: e.target.value })} data-testid="new-menu-description" /></div>
            <div className="space-y-2"><Label>Price ($)</Label><Input type="number" step="0.01" value={newMenuItem.price} onChange={(e) => setNewMenuItem({ ...newMenuItem, price: e.target.value })} data-testid="new-menu-price" /></div>
            <div className="space-y-2"><Label>Category</Label>
              <Select value={newMenuItem.category} onValueChange={(v) => setNewMenuItem({ ...newMenuItem, category: v })}>
                <SelectTrigger data-testid="new-menu-category"><SelectValue /></SelectTrigger>
                <SelectContent data-testid="new-menu-category-options">
                  {(menuCategories && menuCategories.length > 0
                    ? menuCategories
                    : [{ name: "mains" }, { name: "appetizers" }, { name: "drinks" }, { name: "desserts" }]
                  ).map((c) => (
                    <SelectItem key={c.name} value={c.name}>
                      {c.name.replace(/\b\w/g, (ch) => ch.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Image URL</Label><Input value={newMenuItem.image_url} onChange={(e) => setNewMenuItem({ ...newMenuItem, image_url: e.target.value })} data-testid="new-menu-image" /></div>
            <BarcodeField
              value={newMenuItem.barcode}
              onChange={(v) => setNewMenuItem({ ...newMenuItem, barcode: v })}
              testIdPrefix="new-menu-barcode"
            />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowMenuDialog(false)}>Cancel</Button><Button onClick={handleCreateMenuItem} data-testid="save-menu-item-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Employee</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2"><Label>Name</Label><Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="new-user-name" /></div>
            <div className="space-y-2"><Label>4-Digit PIN</Label><Input type="password" maxLength={4} placeholder="0000" value={newUser.pin} onChange={(e) => setNewUser({ ...newUser, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} className="font-mono text-center text-xl tracking-[8px]" data-testid="new-user-pin" /></div>
            <div className="space-y-2"><Label>Email (optional)</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-user-email" /></div>
            <div className="space-y-2"><Label>Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="server">Server</SelectItem>
                  <SelectItem value="cashier">Cashier</SelectItem>
                  {storeSettings.first_party_delivery_enabled && (
                    <SelectItem value="driver" data-testid="role-option-driver-new">Driver</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Hourly Rate ($)</Label><Input type="number" step="0.01" min="0" placeholder="0.00" value={newUser.hourly_rate} onChange={(e) => setNewUser({ ...newUser, hourly_rate: e.target.value })} data-testid="new-user-hourly-rate" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowUserDialog(false)}>Cancel</Button><Button onClick={handleCreateUser} data-testid="save-user-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Edit Employee</DialogTitle></DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-4">
              <div className="space-y-2"><Label>Name</Label><Input value={editingUser.name} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} data-testid="edit-user-name" /></div>
              <div className="space-y-2"><Label>Email</Label><Input value={editingUser.email || ""} onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })} data-testid="edit-user-email" /></div>
              <div className="space-y-2"><Label>Role</Label>
                <Select value={editingUser.role} onValueChange={(v) => setEditingUser({ ...editingUser, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="server">Server</SelectItem>
                    <SelectItem value="cashier">Cashier</SelectItem>
                    {(storeSettings.first_party_delivery_enabled || editingUser.role === "driver") && (
                      <SelectItem value="driver" data-testid="role-option-driver-edit">Driver</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>New PIN (leave empty to keep current)</Label><Input type="password" maxLength={4} placeholder="****" value={editingUser.newPin} onChange={(e) => setEditingUser({ ...editingUser, newPin: e.target.value.replace(/\D/g, "").slice(0, 4) })} className="font-mono text-center text-xl tracking-[8px]" data-testid="edit-user-pin" /></div>
              <div className="space-y-2"><Label>Hourly Rate ($)</Label><Input type="number" step="0.01" min="0" placeholder="0.00" value={editingUser.hourly_rate ?? ""} onChange={(e) => setEditingUser({ ...editingUser, hourly_rate: e.target.value })} data-testid="edit-user-hourly-rate" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button><Button onClick={handleUpdateUser} data-testid="save-edit-user-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Menu Item (Full Editor with Modifier Groups + Location Pricing) */}
      <Dialog open={!!editingMenu} onOpenChange={() => setEditingMenu(null)}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto" data-testid="edit-menu-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Edit Menu Item</DialogTitle></DialogHeader>
          {editingMenu && (
            <div className="space-y-5 py-2">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Name</Label><Input value={editingMenu.name} onChange={(e) => setEditingMenu(m => ({ ...m, name: e.target.value }))} data-testid="edit-menu-name" /></div>
                <div className="space-y-2"><Label>Price ($)</Label><Input type="number" step="0.01" value={editingMenu.price} onChange={(e) => setEditingMenu(m => ({ ...m, price: e.target.value }))} data-testid="edit-menu-price" /></div>
              </div>
              <div className="space-y-2"><Label>Description</Label><Input value={editingMenu.description} onChange={(e) => setEditingMenu(m => ({ ...m, description: e.target.value }))} data-testid="edit-menu-desc" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Category</Label>
                  <Select value={editingMenu.category} onValueChange={(v) => setEditingMenu(m => ({ ...m, category: v }))}>
                    <SelectTrigger data-testid="edit-menu-cat"><SelectValue /></SelectTrigger>
                    <SelectContent data-testid="new-menu-category-options">
                  {(menuCategories && menuCategories.length > 0
                    ? menuCategories
                    : [{ name: "mains" }, { name: "appetizers" }, { name: "drinks" }, { name: "desserts" }]
                  ).map((c) => (
                    <SelectItem key={c.name} value={c.name}>
                      {c.name.replace(/\b\w/g, (ch) => ch.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Image URL</Label><Input value={editingMenu.image_url || ""} onChange={(e) => setEditingMenu(m => ({ ...m, image_url: e.target.value }))} data-testid="edit-menu-img" /></div>
                <BarcodeField
                  value={editingMenu.barcode}
                  onChange={(v) => setEditingMenu(m => ({ ...m, barcode: v }))}
                  testIdPrefix="edit-menu-barcode"
                />
              </div>

              {/* Modifier Groups - assign from templates */}
              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base font-medium">Assigned Modifier Groups</Label>
                </div>
                
                {/* Currently assigned */}
                {(editingMenu.modifier_groups || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground mb-3">No modifiers assigned. Add from templates below.</p>
                ) : (
                  <div className="space-y-3 mb-3">
                    {(editingMenu.modifier_groups || []).map((group, gIdx) => {
                      // Look up the source template so we can surface
                      // its default price next to each option — makes
                      // per-item overrides discoverable without hunting
                      // through Settings → Modifier Templates.
                      const template = modifierTemplates.find(t => t.group_name === group.group_name);
                      return (
                        <div key={gIdx} className="bg-secondary/30 rounded-lg p-3 border border-border" data-testid={`assigned-mod-${gIdx}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="font-medium text-sm">{group.group_name}</span>
                              <span className="text-xs text-muted-foreground ml-2">
                                {group.required && "Required • "}
                                {group.max_select === 1 && "Pick 1"}
                                {group.max_select > 1 && `Pick up to ${group.max_select}`}
                              </span>
                            </div>
                            <Button
                              variant="ghost" size="icon" className="h-7 w-7 text-destructive flex-shrink-0"
                              onClick={() => setEditingMenu(m => ({ ...m, modifier_groups: m.modifier_groups.filter((_, i) => i !== gIdx) }))}
                              data-testid={`remove-mod-group-${gIdx}`}
                            >
                              <Trash size={14} />
                            </Button>
                          </div>
                          <div className="space-y-1.5">
                            {(group.options || []).map((o, oIdx) => {
                              const tmplOption = template?.options?.find(to => to.name === o.name);
                              const defaultPrice = tmplOption ? Number(tmplOption.price) || 0 : null;
                              const currentPrice = Number(o.price) || 0;
                              const differs = defaultPrice !== null && Math.abs(defaultPrice - currentPrice) > 0.001;
                              return (
                                <div key={oIdx} className="flex items-center gap-2" data-testid={`assigned-mod-${gIdx}-opt-${oIdx}`}>
                                  <span className="text-sm flex-1 truncate">{o.name}</span>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-muted-foreground">+$</span>
                                    <Input
                                      type="number"
                                      step="0.25"
                                      min="0"
                                      value={o.price ?? 0}
                                      onChange={(e) => {
                                        const val = parseFloat(e.target.value);
                                        setEditingMenu(m => {
                                          const groups = [...(m.modifier_groups || [])];
                                          const opts = [...(groups[gIdx].options || [])];
                                          opts[oIdx] = { ...opts[oIdx], price: isNaN(val) ? 0 : val };
                                          groups[gIdx] = { ...groups[gIdx], options: opts };
                                          return { ...m, modifier_groups: groups };
                                        });
                                      }}
                                      className="h-7 w-20 font-mono text-sm"
                                      data-testid={`mod-price-${gIdx}-${oIdx}`}
                                    />
                                    {differs && (
                                      <Button
                                        variant="ghost" size="sm"
                                        className="h-7 px-2 text-xs text-muted-foreground hover:text-primary"
                                        title={`Reset to template default $${defaultPrice.toFixed(2)}`}
                                        onClick={() => setEditingMenu(m => {
                                          const groups = [...(m.modifier_groups || [])];
                                          const opts = [...(groups[gIdx].options || [])];
                                          opts[oIdx] = { ...opts[oIdx], price: defaultPrice };
                                          groups[gIdx] = { ...groups[gIdx], options: opts };
                                          return { ...m, modifier_groups: groups };
                                        })}
                                        data-testid={`reset-mod-price-${gIdx}-${oIdx}`}
                                      >
                                        Reset
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            {template && group.options && group.options.length > 0 && (
                              <p className="text-[10px] text-muted-foreground mt-1">
                                Template defaults: {template.options.map(to => `${to.name} +$${(Number(to.price) || 0).toFixed(2)}`).join(" · ")}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add from templates */}
                {modifierTemplates.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Add from modifier library:</p>
                    <div className="flex flex-wrap gap-2">
                      {modifierTemplates
                        .filter(t => !(editingMenu.modifier_groups || []).some(g => g.group_name === t.group_name))
                        .map(t => (
                          <Button key={t.id} variant="outline" size="sm" className="text-xs h-7" onClick={() => {
                            setEditingMenu(m => ({
                              ...m,
                              modifier_groups: [...(m.modifier_groups || []), { group_name: t.group_name, required: t.required, min_select: t.min_select || 0, max_select: t.max_select, options: t.options }]
                            }));
                          }} data-testid={`assign-tmpl-${t.id}`}>
                            <Plus size={12} className="mr-1" /> {t.group_name}
                          </Button>
                        ))
                      }
                    </div>
                  </div>
                )}
              </div>

              {/* Item-specific modifiers (one-offs, no group) */}
              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="text-base font-medium">Item-specific Modifiers</Label>
                    <p className="text-xs text-muted-foreground">Quick add-ons unique to this item (no group). Shows as a flat list in the modifier picker.</p>
                  </div>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => setEditingMenu(m => ({
                    ...m,
                    modifiers: [...(m.modifiers || []), { name: "", price: 0 }]
                  }))} data-testid="add-item-modifier-btn">
                    <Plus size={14} className="mr-1" /> Add
                  </Button>
                </div>
                {(editingMenu.modifiers || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No item-specific modifiers.</p>
                ) : (
                  <div className="space-y-2">
                    {(editingMenu.modifiers || []).map((mod, mIdx) => (
                      <div key={mIdx} className="flex items-center gap-2" data-testid={`item-modifier-${mIdx}`}>
                        <Input
                          value={mod.name || ""}
                          onChange={(e) => setEditingMenu(m => ({
                            ...m,
                            modifiers: m.modifiers.map((x, i) => i === mIdx ? { ...x, name: e.target.value } : x)
                          }))}
                          placeholder="e.g. Extra sauce"
                          className="flex-1 h-9"
                          data-testid={`item-modifier-name-${mIdx}`}
                        />
                        <div className="relative w-28">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={mod.price ?? 0}
                            onChange={(e) => setEditingMenu(m => ({
                              ...m,
                              modifiers: m.modifiers.map((x, i) => i === mIdx ? { ...x, price: parseFloat(e.target.value) || 0 } : x)
                            }))}
                            className="h-9 pl-5 font-mono text-sm"
                            data-testid={`item-modifier-price-${mIdx}`}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => setEditingMenu(m => ({
                            ...m,
                            modifiers: m.modifiers.filter((_, i) => i !== mIdx)
                          }))}
                          data-testid={`remove-item-modifier-${mIdx}`}
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Location Pricing */}
              {locations.length > 0 && (
                <div className="border-t border-border pt-4">
                  <Label className="text-base font-medium mb-3 block">Location Pricing</Label>
                  <p className="text-xs text-muted-foreground mb-3">Override the default price for specific locations. Leave empty to use default (${parseFloat(editingMenu.price || 0).toFixed(2)}).</p>
                  <div className="space-y-2">
                    {locations.map(loc => {
                      const locPrice = (editingMenu.location_prices || []).find(lp => lp.location_id === loc.id);
                      return (
                        <div key={loc.id} className="flex items-center gap-3 bg-secondary/20 rounded-lg px-3 py-2" data-testid={`loc-price-${loc.id}`}>
                          <span className="text-sm flex-1">{loc.name}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-muted-foreground">$</span>
                            <Input
                              type="number" step="0.01" placeholder={String(editingMenu.price)}
                              value={locPrice?.price ?? ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                let prices = [...(editingMenu.location_prices || [])];
                                const existing = prices.findIndex(lp => lp.location_id === loc.id);
                                if (val === "" || val === undefined) {
                                  prices = prices.filter(lp => lp.location_id !== loc.id);
                                } else if (existing >= 0) {
                                  prices[existing] = { ...prices[existing], price: parseFloat(val) };
                                } else {
                                  prices.push({ location_id: loc.id, location_name: loc.name, price: parseFloat(val) });
                                }
                                setEditingMenu(m => ({ ...m, location_prices: prices }));
                              }}
                              className="w-24 h-8 text-sm font-mono"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingMenu(null)}>Cancel</Button>
            <Button onClick={handleUpdateMenuItem} data-testid="save-edit-menu-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Modifier Template */}
      <Dialog open={showModifierDialog} onOpenChange={setShowModifierDialog}>
        <DialogContent className="sm:max-w-[500px]" data-testid="create-modifier-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">New Modifier Group</DialogTitle></DialogHeader>
          <ModifierTemplateForm onSave={(data) => handleCreateModifierTemplate(data)} onCancel={() => setShowModifierDialog(false)} />
        </DialogContent>
      </Dialog>

      {/* Edit Modifier Template */}
      <Dialog open={!!editingModifier} onOpenChange={() => setEditingModifier(null)}>
        <DialogContent className="sm:max-w-[500px]" data-testid="edit-modifier-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Edit: {editingModifier?.group_name}</DialogTitle></DialogHeader>
          {editingModifier && (
            <ModifierTemplateForm
              initial={editingModifier}
              onSave={() => handleUpdateModifierTemplate()}
              onCancel={() => setEditingModifier(null)}
              editing={editingModifier}
              setEditing={setEditingModifier}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Equipment Dialog */}
      <Dialog open={showEquipDialog} onOpenChange={(o) => { if (!o) { setShowEquipDialog(false); setEditingEquip(null); } else if (equipForm.type === "printer") {
        // Fetch USB device candidates when the dialog opens for a printer.
        axios.get(`${API}/printers/usb-devices`).then(r => setUsbPrinterDevices(r.data?.devices || [])).catch(() => setUsbPrinterDevices([]));
      } }}>
        <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto" data-testid="equipment-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editingEquip ? "Edit Device" : "Add Device"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Device Name</Label><Input value={equipForm.name} onChange={(e) => setEquipForm({ ...equipForm, name: e.target.value })} placeholder="e.g. Kitchen Printer 1" data-testid="equipment-name" /></div>
              <div className="space-y-2"><Label>Type</Label>
                <Select value={equipForm.type} onValueChange={(v) => {
                  setEquipForm({ ...equipForm, type: v });
                  if (v === "printer" && usbPrinterDevices.length === 0) {
                    axios.get(`${API}/printers/usb-devices`).then(r => setUsbPrinterDevices(r.data?.devices || [])).catch(() => {});
                  }
                }}>
                  <SelectTrigger data-testid="equipment-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{EQUIP_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {equipForm.type === "printer" && (
              <div className="space-y-2">
                <Label>Connection Type</Label>
                <Select
                  value={equipForm.connection_type || "network"}
                  onValueChange={(v) => setEquipForm({ ...equipForm, connection_type: v })}
                >
                  <SelectTrigger data-testid="equipment-connection-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="network" data-testid="conn-network">Network (Ethernet / Wi-Fi, TCP 9100)</SelectItem>
                    <SelectItem value="usb" data-testid="conn-usb">USB (on-prem host, /dev/usb/lp*)</SelectItem>
                    <SelectItem value="webusb" data-testid="conn-webusb">USB via browser (WebUSB — cloud POS)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {equipForm.type === "printer" && (equipForm.connection_type || "network") === "network" && (
              <div className="space-y-2">
                <Label>IP Address</Label>
                <div className="flex gap-2">
                  <Input
                    value={equipForm.ip_address}
                    onChange={(e) => setEquipForm({ ...equipForm, ip_address: e.target.value })}
                    placeholder="192.168.1.100"
                    className="font-mono"
                    data-testid="equipment-ip"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={!equipForm.ip_address || ipProbing}
                    onClick={async () => {
                      setIpProbing(true);
                      try {
                        const { data } = await axios.get(`${API}/printers/probe-ip`, { params: { ip: equipForm.ip_address } });
                        const patch = {};
                        if (data.mac_address) patch.mac_address = data.mac_address;
                        setEquipForm((prev) => ({ ...prev, ...patch }));
                        if (data.mac_address && data.port_9100_open) {
                          toast.success(`Detected: MAC ${data.mac_address} · ESC/POS port open`);
                        } else if (data.mac_address) {
                          toast.success(`MAC detected: ${data.mac_address} (port 9100 closed — power on / check firmware)`);
                        } else if (data.port_9100_open) {
                          toast.warning("Port 9100 open, but MAC couldn't be read from ARP. Try the Re-detect button once the printer has responded to a ping.");
                        } else {
                          toast.error("No response from that IP. Check power, cabling and the address.");
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Detection failed");
                      } finally {
                        setIpProbing(false);
                      }
                    }}
                    data-testid="equipment-detect-btn"
                  >
                    {ipProbing ? "Detecting…" : "Detect"}
                  </Button>
                </div>
                {equipForm.mac_address && (
                  <p className="text-xs text-muted-foreground">
                    MAC: <span className="font-mono" data-testid="equipment-detected-mac">{equipForm.mac_address}</span>
                  </p>
                )}
              </div>
            )}
            {equipForm.type === "printer" && (equipForm.connection_type || "network") === "usb" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>USB Device</Label>
                  <button
                    type="button"
                    onClick={() => axios.get(`${API}/printers/usb-devices`).then(r => { setUsbPrinterDevices(r.data?.devices || []); toast.success("Scanned USB ports"); }).catch(() => toast.error("Scan failed"))}
                    className="text-xs text-primary hover:underline"
                    data-testid="usb-rescan-btn"
                  >
                    Re-scan
                  </button>
                </div>
                <Select
                  value={equipForm.device_path || ""}
                  onValueChange={(v) => setEquipForm({ ...equipForm, device_path: v })}
                >
                  <SelectTrigger data-testid="equipment-device-path"><SelectValue placeholder="Select USB device path..." /></SelectTrigger>
                  <SelectContent>
                    {usbPrinterDevices.map((d) => (
                      <SelectItem key={d.path} value={d.path} data-testid={`usb-device-${d.path.replace(/\//g, "-")}`}>
                        <span className="font-mono">{d.path}</span>
                        {d.writable ? <span className="ml-2 text-green-600 dark:text-green-400">• detected</span> : <span className="ml-2 text-muted-foreground">• not detected</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {equipForm.device_path && !usbPrinterDevices.find(d => d.path === equipForm.device_path)?.writable && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Path saved but the device isn&apos;t currently writable. Plug the printer in and add the POS user to the <code>lp</code> group if permission errors persist.
                  </p>
                )}
                <div className="space-y-2 pt-2">
                  <Label className="text-xs text-muted-foreground">…or type a custom path</Label>
                  <Input value={equipForm.device_path || ""} onChange={(e) => setEquipForm({ ...equipForm, device_path: e.target.value })} placeholder="/dev/usb/lp0" data-testid="equipment-device-path-input" />
                </div>
              </div>
            )}
            {equipForm.type === "printer" && (equipForm.connection_type || "network") === "webusb" && (
              <div className="space-y-3 rounded-lg border border-border p-3 bg-secondary/20">
                <div className="text-xs text-muted-foreground leading-relaxed">
                  WebUSB lets this browser talk directly to a plugged-in USB thermal printer — no server-side USB access needed. Pair once per terminal browser; the permission is remembered across restarts.
                </div>
                {!webusbSupported() && (
                  <div className="text-xs text-red-600 dark:text-red-400">
                    This browser doesn&apos;t support WebUSB. Use Chrome, Edge, or Brave on the terminal.
                  </div>
                )}
                {equipForm.webusb_vendor_id ? (
                  <div className="text-sm space-y-1">
                    <div>
                      <span className="text-muted-foreground">Paired:</span>{" "}
                      <span className="font-medium" data-testid="webusb-paired-name">
                        {equipForm.webusb_display_name || "USB Printer"}
                      </span>
                    </div>
                    <div className="text-xs font-mono text-muted-foreground">
                      VID {Number(equipForm.webusb_vendor_id).toString(16).padStart(4, "0")}
                      {" "}PID {Number(equipForm.webusb_product_id).toString(16).padStart(4, "0")}
                      {equipForm.webusb_serial_number ? ` · SN ${equipForm.webusb_serial_number}` : ""}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">Not paired yet.</div>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={!webusbSupported()}
                    onClick={async () => {
                      try {
                        const info = await pairPrinter();
                        if (!info) return;
                        setEquipForm((prev) => ({
                          ...prev,
                          webusb_vendor_id: info.vendorId,
                          webusb_product_id: info.productId,
                          webusb_serial_number: info.serialNumber || "",
                          webusb_display_name: info.displayName,
                        }));
                        toast.success(`Paired: ${info.displayName}`);
                      } catch (e) {
                        // NotFoundError = user closed the picker without choosing.
                        if (e?.name !== "NotFoundError") {
                          toast.error(e?.message || "Pairing failed");
                        }
                      }
                    }}
                    data-testid="webusb-pair-btn"
                  >
                    {equipForm.webusb_vendor_id ? "Re-pair Printer" : "Pair USB Printer"}
                  </Button>
                  {equipForm.webusb_vendor_id && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setEquipForm((prev) => ({
                        ...prev,
                        webusb_vendor_id: null,
                        webusb_product_id: null,
                        webusb_serial_number: "",
                        webusb_display_name: "",
                      }))}
                      data-testid="webusb-unpair-btn"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            )}
            {equipForm.type !== "printer" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>IP Address</Label><Input value={equipForm.ip_address} onChange={(e) => setEquipForm({ ...equipForm, ip_address: e.target.value })} placeholder="192.168.1.100" data-testid="equipment-ip" /></div>
                <div className="space-y-2"><Label>MAC Address</Label><Input value={equipForm.mac_address} onChange={(e) => setEquipForm({ ...equipForm, mac_address: e.target.value })} data-testid="equipment-mac" /></div>
              </div>
            )}

            {/* Advanced / rarely-changed fields tucked behind a toggle so
                the primary flow is just Name + IP (or USB path). */}
            <button
              type="button"
              onClick={() => setShowEquipAdvanced((v) => !v)}
              className="text-xs text-primary hover:underline"
              data-testid="equipment-toggle-advanced"
            >
              {showEquipAdvanced ? "Hide advanced fields" : "Show advanced fields (model, serial, location, station…)"}
            </button>
            {showEquipAdvanced && (
              <div className="space-y-4 rounded-lg border border-dashed border-border p-4 bg-secondary/20">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Model</Label><Input value={equipForm.model_name} onChange={(e) => setEquipForm({ ...equipForm, model_name: e.target.value })} placeholder="e.g. Epson TM-T88VI" data-testid="equipment-model" /></div>
                  <div className="space-y-2"><Label>Serial Number</Label><Input value={equipForm.serial_number} onChange={(e) => setEquipForm({ ...equipForm, serial_number: e.target.value })} data-testid="equipment-serial" /></div>
                </div>
                {equipForm.type === "printer" && (equipForm.connection_type || "network") === "network" && (
                  <div className="space-y-2">
                    <Label>MAC Address (auto-filled by Detect)</Label>
                    <Input value={equipForm.mac_address} onChange={(e) => setEquipForm({ ...equipForm, mac_address: e.target.value })} placeholder="Detect will fill this from the IP" data-testid="equipment-mac" />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Location</Label><Input value={equipForm.location} onChange={(e) => setEquipForm({ ...equipForm, location: e.target.value })} placeholder="Kitchen, Bar" data-testid="equipment-location" /></div>
                  <div className="space-y-2"><Label>Station</Label><Input value={equipForm.assigned_station} onChange={(e) => setEquipForm({ ...equipForm, assigned_station: e.target.value })} placeholder="POS 1" data-testid="equipment-station" /></div>
                </div>
              </div>
            )}
            {["workstation", "tablet", "handheld"].includes(equipForm.type) && (
              <div className="space-y-2">
                <Label>Receipt Printer</Label>
                <Select value={equipForm.assigned_printer || "none"} onValueChange={(v) => setEquipForm({ ...equipForm, assigned_printer: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="equipment-printer"><SelectValue placeholder="Select printer..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {equipment.filter(e => e.type === "printer").map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}{p.location ? ` (${p.location})` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2"><Label>Status</Label>
              <Select value={equipForm.status} onValueChange={(v) => setEquipForm({ ...equipForm, status: v })}>
                <SelectTrigger data-testid="equipment-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="online">Online</SelectItem><SelectItem value="offline">Offline</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => { setShowEquipDialog(false); setEditingEquip(null); }}>Cancel</Button><Button onClick={handleSaveEquip} data-testid="save-equipment-btn">{editingEquip ? "Save" : "Add Device"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Network Dialog */}
      <Dialog open={showNetDialog} onOpenChange={(o) => { if (!o) { setShowNetDialog(false); setEditingNet(null); } }}>
        <DialogContent className="sm:max-w-[550px] max-h-[85vh] overflow-y-auto" data-testid="network-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editingNet ? "Edit Network" : "Add Network"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Network Name</Label><Input value={netForm.name} onChange={(e) => setNetForm({ ...netForm, name: e.target.value })} placeholder="POS Main" data-testid="network-name" /></div>
              <div className="space-y-2"><Label>SSID</Label><Input value={netForm.ssid} onChange={(e) => setNetForm({ ...netForm, ssid: e.target.value })} placeholder="Restaurant-POS" data-testid="network-ssid" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Security</Label>
                <Select value={netForm.security_type} onValueChange={(v) => setNetForm({ ...netForm, security_type: v })}>
                  <SelectTrigger data-testid="network-security"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="WPA2">WPA2</SelectItem><SelectItem value="WPA3">WPA3</SelectItem><SelectItem value="WPA2-Enterprise">WPA2-Enterprise</SelectItem><SelectItem value="WPA3-Enterprise">WPA3-Enterprise</SelectItem><SelectItem value="Open">Open</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Password</Label><Input type="password" value={netForm.password} onChange={(e) => setNetForm({ ...netForm, password: e.target.value })} data-testid="network-password" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>IP Range</Label><Input value={netForm.ip_range} onChange={(e) => setNetForm({ ...netForm, ip_range: e.target.value })} placeholder="192.168.1.0/24" data-testid="network-ip-range" /></div>
              <div className="space-y-2"><Label>Gateway</Label><Input value={netForm.gateway} onChange={(e) => setNetForm({ ...netForm, gateway: e.target.value })} placeholder="192.168.1.1" data-testid="network-gateway" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Primary DNS</Label><Input value={netForm.dns_primary} onChange={(e) => setNetForm({ ...netForm, dns_primary: e.target.value })} placeholder="8.8.8.8" data-testid="network-dns1" /></div>
              <div className="space-y-2"><Label>VLAN ID</Label><Input value={netForm.vlan_id} onChange={(e) => setNetForm({ ...netForm, vlan_id: e.target.value })} data-testid="network-vlan" /></div>
            </div>
            <div className="space-y-2"><Label>Purpose</Label><Input value={netForm.purpose} onChange={(e) => setNetForm({ ...netForm, purpose: e.target.value })} placeholder="POS terminals, kitchen displays" data-testid="network-purpose" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => { setShowNetDialog(false); setEditingNet(null); }}>Cancel</Button><Button onClick={handleSaveNet} data-testid="save-network-btn">{editingNet ? "Save" : "Add Network"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logo crop / rotate modal — opens after file pick, before persist. */}
      <LogoCropModal
        open={!!cropSource}
        sourceUrl={cropSource}
        onCancel={() => setCropSource(null)}
        onSave={handleCroppedLogo}
      />
    </div>
  );
};

export default SettingsPage;
